export type Locale = "en" | "ar" | "de" | "es" | "fr" | "he" | "hi" | "id" | "it" | "pt" | "ru" | "tr";

export const localeNames: Record<Locale, string> = {
  en: "English",
  ar: "العربية",
  de: "Deutsch",
  es: "Español",
  fr: "Français",
  he: "עברית",
  hi: "हिन्दी",
  id: "Indonesia",
  it: "Italiano",
  pt: "Português",
  ru: "Русский",
  tr: "Türkçe",
};

export const rtlLocales: Locale[] = ["ar", "he"];

export const translations: Record<Locale, Record<string, string>> = {
  en: {
    // Header
    "nav.features": "Features",
    "nav.howItWorks": "How It Works",
    "nav.pricing": "Pricing",
    "nav.faq": "FAQ",
    "nav.cta": "Connect WhatsApp →",

    // Hero
    "hero.badge": "WhatsApp Reminders",
    "hero.title": "Never Forget",
    "hero.titleHighlight": "a Task Again",
    "hero.subtitle":
      "Get reminders directly in WhatsApp. No app switching, no forgotten tasks – just message and done.",
    "hero.cta": "Connect WhatsApp →",
    "hero.socialProof": "people started today",
    "hero.rating": "from 651K reviews",

    // Features
    "features.title": "Task Management,",
    "features.titleHighlight": "Simplified",
    "features.subtitle": "Everything you need to stay organized, right inside the app you already use every day.",
    "features.justMessage.title": "Just Message",
    "features.justMessage.desc": '"Remind me to pay rent on the 1st" – it\'s that simple.',
    "features.voiceTasks.title": "Voice Tasks",
    "features.voiceTasks.desc": "Send a voice note and we'll transcribe it into tasks. Speak naturally, we understand.",
    "features.forwardToCreate.title": "Forward to Create",
    "features.forwardToCreate.desc": "Got a message with something to do? Forward it to the bot and it becomes a task.",
    "features.multipleTasks.title": "Multiple Tasks at Once",
    "features.multipleTasks.desc": 'Say "Buy milk, call mom, and send the report" – we\'ll create 3 separate tasks.',
    "features.snoozeComplete.title": "Snooze & Complete",
    "features.snoozeComplete.desc":
      "Tap to snooze a reminder or mark it done. Stay in control without leaving WhatsApp.",
    "features.attachFiles.title": "Attach Files",
    "features.attachFiles.desc": "Add photos, documents, and files to your tasks. Keep everything in one place.",
    "features.languages.title": "29+ Languages",
    "features.languages.desc": "Speak your language. We support English, Spanish, Hebrew, Arabic, and many more.",
    "features.new": "NEW",

    // Power Features
    "powerFeatures.badge": "✨ Power Features",
    "powerFeatures.title": "Do More,",
    "powerFeatures.titleHighlight": "Faster",
    "powerFeatures.subtitle": "Advanced capabilities that make task management feel effortless.",
    "powerFeatures.voiceToTasks.title": "Voice to Tasks",
    "powerFeatures.voiceToTasks.desc":
      "Send a voice note with multiple tasks. \"Buy milk, schedule dentist appointment, and email the team about Friday's meeting\" – we'll understand and create separate tasks for each.",
    "powerFeatures.organizeIntoLists.title": "Organize into Lists",
    "powerFeatures.organizeIntoLists.desc":
      'Say "Add to my shopping list" or "Put this in work tasks" and we\'ll file it exactly where you want. Your lists, your organization.',
    "powerFeatures.smartSnoozing.title": "Smart Snoozing",
    "powerFeatures.smartSnoozing.desc":
      'Not ready yet? Tap "Snooze 2 hours" or "Remind me tomorrow at 9am". Mark tasks complete with a simple tap.',
    "powerFeatures.languageSupport.title": "Speak Your Language",
    "powerFeatures.languageSupport.desc":
      "Create tasks in 29+ languages. English, Spanish, Hebrew, Arabic, French, and many more – we understand them all.",
    "powerFeatures.moreLanguages": "+17 more",
    "powerFeatures.fileAttachments.title": "File Attachments",
    "powerFeatures.fileAttachments.desc":
      "Attach photos, receipts, documents to any task. Just send the file and tell us which task it belongs to.",
    "powerFeatures.smartListAssignment.title": "Smart List Assignment",
    "powerFeatures.smartListAssignment.desc":
      '"Add eggs to my shopping list" – we know exactly where it goes. Create and manage all your lists via chat.',

    // How It Works
    "howItWorks.title": "How It",
    "howItWorks.titleHighlight": "Works",
    "howItWorks.subtitle": "Get started in seconds. No complex setup, no learning curve.",
    "howItWorks.step1.title": "Start a Chat",
    "howItWorks.step1.desc": "Open WhatsApp and start a conversation with the Any.do bot.",
    "howItWorks.step2.title": "Create Your Task",
    "howItWorks.step2.desc": "Type, speak, or forward a message. Add to any list you want.",
    "howItWorks.step3.title": "Get Reminded",
    "howItWorks.step3.desc": "Receive a WhatsApp message when it's time. Tap to snooze or complete.",

    // Social Proof
    "socialProof.title": "Loved by",
    "socialProof.titleHighlight": "Millions",
    "socialProof.subtitle": "Join the community of people who've transformed their productivity.",
    "socialProof.users": "Users",
    "socialProof.reviews": "5-Star Reviews",
    "socialProof.appStoreRating": "App Store Rating",
    "socialProof.countries": "Countries",
    "socialProof.review1.text":
      "Finally, a to-do app that fits into my life! I literally never leave WhatsApp, so this is perfect.",
    "socialProof.review1.author": "Sarah M.",
    "socialProof.review1.role": "Marketing Manager",
    "socialProof.review2.text":
      "The forwarding feature is genius. Someone sends me something to do, I forward it, done. No more forgotten tasks.",
    "socialProof.review2.author": "James K.",
    "socialProof.review2.role": "Entrepreneur",
    "socialProof.review3.text":
      "I love getting reminders right in WhatsApp. No more switching apps or missing important tasks. Life-changing!",
    "socialProof.review3.author": "Maria L.",
    "socialProof.review3.role": "Freelance Designer",

    // Pricing
    "pricing.badge": "Special Offer – 50% OFF",
    "pricing.title": "Start Organizing",
    "pricing.titleHighlight": "Today",
    "pricing.subtitle": "Get premium access at an unbeatable price. Limited time only.",
    "pricing.offerEnds": "Offer ends in:",
    "pricing.billedAnnually": "Billed annually at $35.88/year",
    "pricing.cta": "Connect WhatsApp →",
    "pricing.cancelAnytime": "Cancel anytime",
    "pricing.feature.unlimitedTasks": "Unlimited tasks & reminders",
    "pricing.feature.whatsapp": "WhatsApp integration",
    "pricing.feature.forward": "Forward to create tasks",
    "pricing.feature.calendar": "Calendar sync",
    "pricing.feature.priority": "Priority support",
    "pricing.feature.sync": "Cross-device sync",
    "pricing.perMonth": "/month",

    // FAQ
    "faq.title": "Frequently Asked",
    "faq.titleHighlight": "Questions",
    "faq.subtitle": "Everything you need to know about Any.do on WhatsApp.",
    "faq.q1": "How does Any.do work with WhatsApp?",
    "faq.a1":
      "Simply message our WhatsApp bot to create tasks, set reminders, and manage your to-do list. You can send voice messages, forward emails or messages, and we'll automatically turn them into organized tasks with smart reminders.",
    "faq.q2": "Is my data private and secure?",
    "faq.a2":
      "Absolutely. We use end-to-end encryption and never share your data with third parties. Your tasks and personal information are stored securely and only accessible by you.",
    "faq.q3": "Do I need to install an app?",
    "faq.a3":
      "You can use Any.do entirely within WhatsApp without downloading anything. However, the Any.do app provides additional capabilities like calendar views, widgets, and offline access for an enhanced experience.",
    "faq.q4": "Can I sync with my calendar?",
    "faq.a4":
      "Yes, Any.do integrates with Google Calendar, Outlook, and Apple Calendar but it's not available via the WhatsApp integration. Your tasks and reminders will automatically sync across all your devices and calendars.",

    // Footer
    "footer.privacy": "Privacy",
    "footer.terms": "Terms",
    "footer.support": "Support",
    "footer.copyright": "All rights reserved.",

    // Exit Intent
    "exitIntent.title": "Get Started in Seconds",
    "exitIntent.subtitle": 'Just send a message like "Remind me to call John today at 5" and you\'re all set.',
    "exitIntent.benefit1": "Voice messages → instant tasks",
    "exitIntent.benefit2": "Smart reminders at the right time",
    "exitIntent.benefit3": "Works in your existing WhatsApp",
    "exitIntent.cta": "Connect WhatsApp →",
    "exitIntent.noCreditCard": "No credit card required • Cancel anytime",

    // Mobile CTA
    mobileCta: "Connect WhatsApp →",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30 AM",
    "whatsapp.hero.msg2": "✅ Added 2 tasks:\n\n📞 **Call mom** - Today at 6:00 PM\n🎁 **Buy birthday gift**",
    "whatsapp.hero.time2": "10:30 AM",
    "whatsapp.hero.msg3": "🔔 **Call mom** is now due!",
    "whatsapp.hero.time3": "6:00 PM",

    // WhatsApp Demo Messages (How It Works)
    "whatsapp.demo.msg1": "Add milk and eggs to shopping list",
    "whatsapp.demo.time1": "9:15 AM",
    "whatsapp.demo.msg2": "Added to Shopping List: milk, eggs 🛒",
    "whatsapp.demo.time2": "9:15 AM",
    "whatsapp.demo.msg3": "Remind me about the meeting at 2pm",
    "whatsapp.demo.time3": "9:16 AM",
    "whatsapp.demo.msg4": "Reminder set for today at 2:00 PM 📅",
    "whatsapp.demo.time4": "9:16 AM",

    // Notifications
    "notification.justStarted": "just started their free trial",
    "notification.minutesAgo": "m ago",
    "notification.from": "from",
    "notification.user1.name": "Sarah M.",
    "notification.user1.location": "New York",
    "notification.user2.name": "James K.",
    "notification.user2.location": "London",
    "notification.user3.name": "Maria L.",
    "notification.user3.location": "Barcelona",
    "notification.user4.name": "Ahmed R.",
    "notification.user4.location": "Dubai",
    "notification.user5.name": "Yuki T.",
    "notification.user5.location": "Tokyo",
    "notification.user6.name": "Anna S.",
    "notification.user6.location": "Berlin",
    "notification.user7.name": "Carlos M.",
    "notification.user7.location": "São Paulo",
    "notification.user8.name": "Priya K.",
    "notification.user8.location": "Mumbai",

    // Auth
    "auth.signIn.title": "Welcome to Any.do",
    "auth.signIn.subtitle": "Sign in or create an account to get started",
    "auth.signIn.cta": "Sign In",
    "auth.continueWithGoogle": "Continue with Google",
    "auth.or": "or",
    "auth.email": "Email",
    "auth.emailPlaceholder": "Enter your email",
    "auth.continue": "Continue",
    "auth.name": "Name",
    "auth.namePlaceholder": "Enter your name",
    "auth.password": "Password",
    "auth.passwordPlaceholder": "Enter your password",
    "auth.createAccount.title": "Create Account",
    "auth.createAccount.cta": "Create Account",
    "auth.welcomeBack.title": "Welcome Back!",
    "auth.verifyCode.title": "Enter Verification Code",
    "auth.verifyCode.subtitle": "We sent a code to",
    "auth.verifyCode.resend": "Resend code",
    "auth.verifyCode.resending": "Sending...",
    "auth.verifyCode.validFor": "Code is valid for 10 minutes",
    "auth.error.generic": "Something went wrong. Please try again.",
    "auth.error.invalidCredentials": "Incorrect email or password.",
    "auth.error.invalidCode": "Invalid verification code.",
    "auth.error.tooManyAttempts": "Too many attempts. Please try again later.",
    "auth.error.passwordTooShort": "Password must be at least 6 characters.",

    // Payment
    "payment.title": "Complete Your Subscription",
    "payment.subtitle": "Start managing tasks via WhatsApp",
    "payment.processing": "Processing...",
    "payment.subscribe": "Subscribe Now",
    "payment.error": "Payment failed. Please try again.",

    // Phone Verification
    "phone.title": "Verify Your Phone",
    "phone.subtitle": "Enter your phone number to connect with WhatsApp",
    "phone.send": "Send Code",
    "phone.verify": "Verify",
    "phone.placeholder": "Phone number",
    "phone.codePlaceholder": "Enter 6-digit code",
    "phone.error.send": "Failed to send code. Please try again.",
    "phone.error.verify": "Invalid code. Please try again.",

    // Workspace
    "workspace.title": "Setting up your workspace...",
    "workspace.subtitle": "We're connecting your WhatsApp. This will take a moment.",
  },

  ar: {
    // Header
    "nav.features": "المميزات",
    "nav.howItWorks": "كيف يعمل",
    "nav.pricing": "الأسعار",
    "nav.faq": "الأسئلة الشائعة",
    "nav.cta": "← اتصل بواتساب",

    // Hero
    "hero.badge": "تذكيرات واتساب",
    "hero.title": "لا تنسَ",
    "hero.titleHighlight": "أي مهمة بعد الآن",
    "hero.subtitle": "احصل على تذكيرات مباشرة في واتساب. لا تبديل للتطبيقات، لا مهام منسية – فقط أرسل وانتهى.",
    "hero.cta": "← اتصل بواتساب",
    "hero.socialProof": "شخص بدأوا اليوم",
    "hero.rating": "من 651 ألف تقييم",

    // Features
    "features.title": "إدارة المهام،",
    "features.titleHighlight": "مبسّطة",
    "features.subtitle": "كل ما تحتاجه للبقاء منظمًا، مباشرة في التطبيق الذي تستخدمه كل يوم.",
    "features.justMessage.title": "فقط أرسل رسالة",
    "features.justMessage.desc": '"ذكرني بدفع الإيجار في الأول من الشهر" – بهذه البساطة.',
    "features.voiceTasks.title": "مهام صوتية",
    "features.voiceTasks.desc": "أرسل رسالة صوتية وسنحولها إلى مهام. تحدث بشكل طبيعي، نحن نفهم.",
    "features.forwardToCreate.title": "أعد التوجيه للإنشاء",
    "features.forwardToCreate.desc": "لديك رسالة فيها شيء للقيام به؟ أعد توجيهها للبوت وستصبح مهمة.",
    "features.multipleTasks.title": "مهام متعددة مرة واحدة",
    "features.multipleTasks.desc": 'قل "اشترِ الحليب، اتصل بأمي، وأرسل التقرير" – سننشئ 3 مهام منفصلة.',
    "features.snoozeComplete.title": "تأجيل وإكمال",
    "features.snoozeComplete.desc": "اضغط لتأجيل تذكير أو وضع علامة تم. ابقَ مسيطرًا دون مغادرة واتساب.",
    "features.attachFiles.title": "إرفاق ملفات",
    "features.attachFiles.desc": "أضف صورًا ومستندات وملفات لمهامك. احتفظ بكل شيء في مكان واحد.",
    "features.languages.title": "+29 لغة",
    "features.languages.desc": "تحدث بلغتك. ندعم العربية والإنجليزية والعبرية والإسبانية والمزيد.",
    "features.new": "جديد",

    // Power Features
    "powerFeatures.badge": "✨ ميزات قوية",
    "powerFeatures.title": "أنجز أكثر،",
    "powerFeatures.titleHighlight": "أسرع",
    "powerFeatures.subtitle": "إمكانيات متقدمة تجعل إدارة المهام سهلة للغاية.",
    "powerFeatures.voiceToTasks.title": "صوت إلى مهام",
    "powerFeatures.voiceToTasks.desc":
      'أرسل رسالة صوتية بمهام متعددة. "اشترِ الحليب، حدد موعد طبيب الأسنان، وأرسل بريدًا للفريق عن اجتماع الجمعة" – سنفهم وننشئ مهام منفصلة.',
    "powerFeatures.organizeIntoLists.title": "نظّم في قوائم",
    "powerFeatures.organizeIntoLists.desc": 'قل "أضف لقائمة التسوق" أو "ضع هذا في مهام العمل" وسنضعه بالضبط حيث تريد.',
    "powerFeatures.smartSnoozing.title": "تأجيل ذكي",
    "powerFeatures.smartSnoozing.desc": 'لست جاهزًا؟ اضغط "تأجيل ساعتين" أو "ذكرني غدًا الساعة 9 صباحًا".',
    "powerFeatures.languageSupport.title": "تحدث بلغتك",
    "powerFeatures.languageSupport.desc":
      "أنشئ مهام بأكثر من 29 لغة. العربية والإنجليزية والعبرية والفرنسية والمزيد – نفهمها جميعًا.",
    "powerFeatures.moreLanguages": "+17 المزيد",
    "powerFeatures.fileAttachments.title": "مرفقات الملفات",
    "powerFeatures.fileAttachments.desc":
      "أرفق صورًا وإيصالات ومستندات لأي مهمة. فقط أرسل الملف وأخبرنا لأي مهمة ينتمي.",
    "powerFeatures.smartListAssignment.title": "تعيين ذكي للقوائم",
    "powerFeatures.smartListAssignment.desc": '"أضف البيض لقائمة التسوق" – نعرف بالضبط أين يذهب.',

    // How It Works
    "howItWorks.title": "كيف",
    "howItWorks.titleHighlight": "يعمل",
    "howItWorks.subtitle": "ابدأ في ثوانٍ. لا إعداد معقد، لا منحنى تعلم.",
    "howItWorks.step1.title": "ابدأ محادثة",
    "howItWorks.step1.desc": "افتح واتساب وابدأ محادثة مع بوت Any.do.",
    "howItWorks.step2.title": "أنشئ مهمتك",
    "howItWorks.step2.desc": "اكتب أو تحدث أو أعد توجيه رسالة. أضف إلى أي قائمة تريدها.",
    "howItWorks.step3.title": "احصل على تذكير",
    "howItWorks.step3.desc": "استلم رسالة واتساب عندما يحين الوقت. اضغط للتأجيل أو الإكمال.",

    // Social Proof
    "socialProof.title": "محبوب من",
    "socialProof.titleHighlight": "الملايين",
    "socialProof.subtitle": "انضم إلى مجتمع الأشخاص الذين حولوا إنتاجيتهم.",
    "socialProof.users": "مستخدم",
    "socialProof.reviews": "تقييم 5 نجوم",
    "socialProof.appStoreRating": "تقييم متجر التطبيقات",
    "socialProof.countries": "دولة",
    "socialProof.review1.text": "أخيرًا، تطبيق مهام يناسب حياتي! أنا حرفيًا لا أغادر واتساب، لذا هذا مثالي.",
    "socialProof.review1.author": "سارة م.",
    "socialProof.review1.role": "مديرة تسويق",
    "socialProof.review2.text": "ميزة إعادة التوجيه عبقرية. شخص يرسل لي شيئًا للقيام به، أعيد توجيهه، انتهى.",
    "socialProof.review2.author": "جيمس ك.",
    "socialProof.review2.role": "رائد أعمال",
    "socialProof.review3.text": "أحب تلقي التذكيرات مباشرة في واتساب. لا مزيد من تبديل التطبيقات. غيّرت حياتي!",
    "socialProof.review3.author": "ماريا ل.",
    "socialProof.review3.role": "مصممة مستقلة",

    // Pricing
    "pricing.badge": "عرض خاص – خصم 50%",
    "pricing.title": "ابدأ التنظيم",
    "pricing.titleHighlight": "اليوم",
    "pricing.subtitle": "احصل على الوصول المميز بسعر لا يُقاوم. لفترة محدودة فقط.",
    "pricing.offerEnds": "ينتهي العرض في:",
    "pricing.billedAnnually": "يُفوتر سنويًا بـ $35.88/سنة",
    "pricing.cta": "← اتصل بواتساب",
    "pricing.cancelAnytime": "إلغاء في أي وقت",
    "pricing.feature.unlimitedTasks": "مهام وتذكيرات غير محدودة",
    "pricing.feature.whatsapp": "تكامل واتساب",
    "pricing.feature.forward": "إعادة التوجيه لإنشاء المهام",
    "pricing.feature.calendar": "مزامنة التقويم",
    "pricing.feature.priority": "دعم ذو أولوية",
    "pricing.feature.sync": "مزامنة عبر الأجهزة",
    "pricing.perMonth": "/شهر",

    // FAQ
    "faq.title": "الأسئلة",
    "faq.titleHighlight": "الشائعة",
    "faq.subtitle": "كل ما تحتاج معرفته عن Any.do على واتساب.",
    "faq.q1": "كيف يعمل Any.do مع واتساب؟",
    "faq.a1":
      "ببساطة أرسل رسالة إلى بوت واتساب لإنشاء مهام وتعيين تذكيرات وإدارة قائمة مهامك. يمكنك إرسال رسائل صوتية أو إعادة توجيه رسائل وسنحولها تلقائيًا إلى مهام.",
    "faq.q2": "هل بياناتي خاصة وآمنة؟",
    "faq.a2": "بالتأكيد. نستخدم التشفير من طرف إلى طرف ولا نشارك بياناتك مع أطراف ثالثة أبدًا.",
    "faq.q3": "هل أحتاج لتثبيت تطبيق؟",
    "faq.a3":
      "يمكنك استخدام Any.do بالكامل داخل واتساب. لكن تطبيق Any.do يوفر إمكانيات إضافية مثل عرض التقويم والودجات.",
    "faq.q4": "هل يمكنني المزامنة مع تقويمي؟",
    "faq.a4": "نعم، Any.do يتكامل مع تقويم Google وOutlook وApple Calendar لكنه غير متوفر عبر تكامل واتساب. ستتم مزامنة مهامك وتذكيراتك تلقائيًا عبر جميع أجهزتك وتقويماتك.",

    // Footer
    "footer.privacy": "الخصوصية",
    "footer.terms": "الشروط",
    "footer.support": "الدعم",
    "footer.copyright": "جميع الحقوق محفوظة.",

    // Exit Intent
    "exitIntent.title": "ابدأ في ثوانٍ",
    "exitIntent.subtitle": 'فقط أرسل رسالة مثل "ذكرني بالاتصال بجون اليوم الساعة 5".',
    "exitIntent.benefit1": "رسائل صوتية ← مهام فورية",
    "exitIntent.benefit2": "تذكيرات ذكية في الوقت المناسب",
    "exitIntent.benefit3": "يعمل في واتساب الخاص بك",
    "exitIntent.cta": "← اتصل بواتساب",
    "exitIntent.noCreditCard": "لا حاجة لبطاقة ائتمان • إلغاء في أي وقت",

    mobileCta: "← اتصل بواتساب",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30 ص",
    "whatsapp.hero.msg2":
      "✅ تمت إضافة مهمتين:\n\n📞 **اتصل بأمي** - اليوم الساعة 6:00 مساءً\n🎁 **اشتري هدية عيد ميلاد**",
    "whatsapp.hero.time2": "10:30 ص",
    "whatsapp.hero.msg3": "🔔 **اتصل بأمي** حان موعدها الآن!",
    "whatsapp.hero.time3": "6:00 م",

    // WhatsApp Demo Messages
    "whatsapp.demo.msg1": "أضف حليب وبيض لقائمة التسوق",
    "whatsapp.demo.time1": "9:15 ص",
    "whatsapp.demo.msg2": "تمت الإضافة لقائمة التسوق: حليب، بيض 🛒",
    "whatsapp.demo.time2": "9:15 ص",
    "whatsapp.demo.msg3": "ذكرني بالاجتماع الساعة 2 مساءً",
    "whatsapp.demo.time3": "9:16 ص",
    "whatsapp.demo.msg4": "تم تعيين التذكير لليوم الساعة 2:00 مساءً 📅",
    "whatsapp.demo.time4": "9:16 ص",

    // Notifications
    "notification.justStarted": "بدأ للتو الفترة التجريبية",
    "notification.minutesAgo": "د",
    "notification.from": "من",
    "notification.user1.name": "سارة م.",
    "notification.user1.location": "دبي",
    "notification.user2.name": "أحمد ك.",
    "notification.user2.location": "القاهرة",
    "notification.user3.name": "ليلى ر.",
    "notification.user3.location": "الرياض",
    "notification.user4.name": "محمد ع.",
    "notification.user4.location": "عمّان",
    "notification.user5.name": "نور س.",
    "notification.user5.location": "بيروت",
    "notification.user6.name": "فاطمة ح.",
    "notification.user6.location": "الدار البيضاء",
    "notification.user7.name": "خالد م.",
    "notification.user7.location": "الكويت",
    "notification.user8.name": "هدى ب.",
    "notification.user8.location": "أبوظبي",
  },

  de: {
    "nav.features": "Funktionen",
    "nav.howItWorks": "So funktioniert's",
    "nav.pricing": "Preise",
    "nav.faq": "FAQ",
    "nav.cta": "WhatsApp verbinden →",

    "hero.badge": "WhatsApp Erinnerungen",
    "hero.title": "Vergiss nie wieder",
    "hero.titleHighlight": "eine Aufgabe",
    "hero.subtitle":
      "Erhalte Erinnerungen direkt in WhatsApp. Kein App-Wechsel, keine vergessenen Aufgaben – einfach schreiben und fertig.",
    "hero.cta": "WhatsApp verbinden →",
    "hero.socialProof": "Personen haben heute angefangen",
    "hero.rating": "von 651K Bewertungen",

    "features.title": "Aufgabenverwaltung,",
    "features.titleHighlight": "vereinfacht",
    "features.subtitle": "Alles was du brauchst um organisiert zu bleiben, direkt in der App die du jeden Tag nutzt.",
    "features.justMessage.title": "Einfach schreiben",
    "features.justMessage.desc": '"Erinnere mich am 1. die Miete zu zahlen" – so einfach ist das.',
    "features.voiceTasks.title": "Sprach-Aufgaben",
    "features.voiceTasks.desc":
      "Sende eine Sprachnachricht und wir erstellen Aufgaben daraus. Sprich natürlich, wir verstehen.",
    "features.forwardToCreate.title": "Weiterleiten zum Erstellen",
    "features.forwardToCreate.desc":
      "Eine Nachricht mit etwas zu tun? Leite sie an den Bot weiter und sie wird zur Aufgabe.",
    "features.multipleTasks.title": "Mehrere Aufgaben gleichzeitig",
    "features.multipleTasks.desc":
      'Sag "Milch kaufen, Mama anrufen und den Bericht senden" – wir erstellen 3 separate Aufgaben.',
    "features.snoozeComplete.title": "Verschieben & Abschließen",
    "features.snoozeComplete.desc": "Tippe um eine Erinnerung zu verschieben oder als erledigt zu markieren.",
    "features.attachFiles.title": "Dateien anhängen",
    "features.attachFiles.desc": "Füge Fotos, Dokumente und Dateien zu deinen Aufgaben hinzu.",
    "features.languages.title": "29+ Sprachen",
    "features.languages.desc": "Sprich deine Sprache. Wir unterstützen Deutsch, Englisch, Spanisch und viele mehr.",
    "features.new": "NEU",

    "powerFeatures.badge": "✨ Power-Funktionen",
    "powerFeatures.title": "Mehr erreichen,",
    "powerFeatures.titleHighlight": "schneller",
    "powerFeatures.subtitle": "Fortgeschrittene Funktionen die Aufgabenverwaltung mühelos machen.",
    "powerFeatures.voiceToTasks.title": "Sprache zu Aufgaben",
    "powerFeatures.voiceToTasks.desc":
      "Sende eine Sprachnachricht mit mehreren Aufgaben. Wir verstehen und erstellen separate Aufgaben für jede.",
    "powerFeatures.organizeIntoLists.title": "In Listen organisieren",
    "powerFeatures.organizeIntoLists.desc": 'Sag "Zur Einkaufsliste hinzufügen" und wir ordnen es genau dort ein.',
    "powerFeatures.smartSnoozing.title": "Intelligentes Verschieben",
    "powerFeatures.smartSnoozing.desc": 'Noch nicht bereit? Tippe "2 Stunden verschieben" oder "Morgen um 9 erinnern".',
    "powerFeatures.languageSupport.title": "Sprich deine Sprache",
    "powerFeatures.languageSupport.desc": "Erstelle Aufgaben in 29+ Sprachen. Deutsch, Englisch, Französisch und mehr.",
    "powerFeatures.moreLanguages": "+17 mehr",
    "powerFeatures.fileAttachments.title": "Dateianhänge",
    "powerFeatures.fileAttachments.desc": "Hänge Fotos, Belege, Dokumente an jede Aufgabe an.",
    "powerFeatures.smartListAssignment.title": "Intelligente Listenzuweisung",
    "powerFeatures.smartListAssignment.desc": '"Eier zur Einkaufsliste" – wir wissen genau wohin.',

    "howItWorks.title": "So funktioniert",
    "howItWorks.titleHighlight": "es",
    "howItWorks.subtitle": "Starte in Sekunden. Kein kompliziertes Setup, keine Lernkurve.",
    "howItWorks.step1.title": "Chat starten",
    "howItWorks.step1.desc": "Öffne WhatsApp und starte ein Gespräch mit dem Any.do Bot.",
    "howItWorks.step2.title": "Aufgabe erstellen",
    "howItWorks.step2.desc": "Tippe, sprich oder leite eine Nachricht weiter. Füge zu jeder Liste hinzu.",
    "howItWorks.step3.title": "Erinnerung erhalten",
    "howItWorks.step3.desc":
      "Erhalte eine WhatsApp-Nachricht wenn es Zeit ist. Tippe zum Verschieben oder Abschließen.",

    "socialProof.title": "Geliebt von",
    "socialProof.titleHighlight": "Millionen",
    "socialProof.subtitle": "Schließe dich der Community an, die ihre Produktivität transformiert hat.",
    "socialProof.users": "Nutzer",
    "socialProof.reviews": "5-Sterne Bewertungen",
    "socialProof.appStoreRating": "App Store Bewertung",
    "socialProof.countries": "Länder",
    "socialProof.review1.text":
      "Endlich eine To-Do App die in mein Leben passt! Ich verlasse WhatsApp nie, also ist das perfekt.",
    "socialProof.review1.author": "Sarah M.",
    "socialProof.review1.role": "Marketing Managerin",
    "socialProof.review2.text":
      "Die Weiterleitungsfunktion ist genial. Jemand schickt mir etwas zu tun, ich leite weiter, erledigt.",
    "socialProof.review2.author": "James K.",
    "socialProof.review2.role": "Unternehmer",
    "socialProof.review3.text": "Ich liebe es, Erinnerungen direkt in WhatsApp zu bekommen. Lebensverändernd!",
    "socialProof.review3.author": "Maria L.",
    "socialProof.review3.role": "Freiberufliche Designerin",

    "pricing.badge": "Sonderangebot – 50% RABATT",
    "pricing.title": "Starte mit Organisieren",
    "pricing.titleHighlight": "heute",
    "pricing.subtitle": "Premium-Zugang zu einem unschlagbaren Preis. Nur für begrenzte Zeit.",
    "pricing.offerEnds": "Angebot endet in:",
    "pricing.billedAnnually": "Jährlich abgerechnet mit $35.88/Jahr",
    "pricing.cta": "WhatsApp verbinden →",
    "pricing.cancelAnytime": "Jederzeit kündbar",
    "pricing.feature.unlimitedTasks": "Unbegrenzte Aufgaben & Erinnerungen",
    "pricing.feature.whatsapp": "WhatsApp Integration",
    "pricing.feature.forward": "Weiterleiten zum Erstellen",
    "pricing.feature.calendar": "Kalender-Sync",
    "pricing.feature.priority": "Prioritäts-Support",
    "pricing.feature.sync": "Geräteübergreifende Sync",
    "pricing.perMonth": "/Monat",

    "faq.title": "Häufig gestellte",
    "faq.titleHighlight": "Fragen",
    "faq.subtitle": "Alles was du über Any.do auf WhatsApp wissen musst.",
    "faq.q1": "Wie funktioniert Any.do mit WhatsApp?",
    "faq.a1":
      "Sende einfach eine Nachricht an unseren WhatsApp-Bot um Aufgaben zu erstellen und Erinnerungen zu setzen.",
    "faq.q2": "Sind meine Daten privat und sicher?",
    "faq.a2": "Absolut. Wir verwenden Ende-zu-Ende-Verschlüsselung und teilen deine Daten nie.",
    "faq.q3": "Muss ich eine App installieren?",
    "faq.a3": "Du kannst Any.do vollständig in WhatsApp nutzen. Die App bietet zusätzliche Funktionen.",
    "faq.q4": "Kann ich mit meinem Kalender synchronisieren?",
    "faq.a4": "Ja, Any.do integriert sich mit Google Calendar, Outlook und Apple Calendar, aber das ist nicht über die WhatsApp-Integration verfügbar. Deine Aufgaben und Erinnerungen werden automatisch über alle deine Geräte und Kalender synchronisiert.",

    "footer.privacy": "Datenschutz",
    "footer.terms": "AGB",
    "footer.support": "Support",
    "footer.copyright": "Alle Rechte vorbehalten.",

    "exitIntent.title": "Starte in Sekunden",
    "exitIntent.subtitle": 'Sende einfach eine Nachricht wie "Erinnere mich, Hans heute um 17 Uhr anzurufen".',
    "exitIntent.benefit1": "Sprachnachrichten → sofortige Aufgaben",
    "exitIntent.benefit2": "Intelligente Erinnerungen zur richtigen Zeit",
    "exitIntent.benefit3": "Funktioniert in deinem WhatsApp",
    "exitIntent.cta": "WhatsApp verbinden →",
    "exitIntent.noCreditCard": "Keine Kreditkarte • Jederzeit kündbar",

    mobileCta: "WhatsApp verbinden →",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30",
    "whatsapp.hero.msg2":
      "✅ 2 Aufgaben hinzugefügt:\n\n📞 **Mama anrufen** - Heute um 18:00 Uhr\n🎁 **Geburtstagsgeschenk kaufen**",
    "whatsapp.hero.time2": "10:30",
    "whatsapp.hero.msg3": "🔔 **Mama anrufen** ist jetzt fällig!",
    "whatsapp.hero.time3": "18:00",

    // WhatsApp Demo Messages
    "whatsapp.demo.msg1": "Milch und Eier zur Einkaufsliste hinzufügen",
    "whatsapp.demo.time1": "9:15",
    "whatsapp.demo.msg2": "Zur Einkaufsliste hinzugefügt: Milch, Eier 🛒",
    "whatsapp.demo.time2": "9:15",
    "whatsapp.demo.msg3": "Erinnere mich an das Meeting um 14 Uhr",
    "whatsapp.demo.time3": "9:16",
    "whatsapp.demo.msg4": "Erinnerung für heute um 14:00 Uhr gesetzt 📅",
    "whatsapp.demo.time4": "9:16",

    // Notifications
    "notification.justStarted": "hat gerade die Testversion gestartet",
    "notification.minutesAgo": "Min",
    "notification.from": "aus",
    "notification.user1.name": "Sarah M.",
    "notification.user1.location": "Berlin",
    "notification.user2.name": "Thomas K.",
    "notification.user2.location": "München",
    "notification.user3.name": "Anna L.",
    "notification.user3.location": "Hamburg",
    "notification.user4.name": "Max R.",
    "notification.user4.location": "Frankfurt",
    "notification.user5.name": "Julia S.",
    "notification.user5.location": "Köln",
    "notification.user6.name": "Felix H.",
    "notification.user6.location": "Wien",
    "notification.user7.name": "Lisa M.",
    "notification.user7.location": "Zürich",
    "notification.user8.name": "David B.",
    "notification.user8.location": "Stuttgart",
  },

  es: {
    "nav.features": "Funciones",
    "nav.howItWorks": "Cómo funciona",
    "nav.pricing": "Precios",
    "nav.faq": "Preguntas",
    "nav.cta": "Conectar WhatsApp →",

    "hero.badge": "Recordatorios de WhatsApp",
    "hero.title": "Nunca olvides",
    "hero.titleHighlight": "una tarea",
    "hero.subtitle":
      "Recibe recordatorios directamente en WhatsApp. Sin cambiar de app, sin tareas olvidadas – solo escribe y listo.",
    "hero.cta": "Conectar WhatsApp →",
    "hero.socialProof": "personas empezaron hoy",
    "hero.rating": "de 651K reseñas",

    "features.title": "Gestión de tareas,",
    "features.titleHighlight": "simplificada",
    "features.subtitle": "Todo lo que necesitas para mantenerte organizado, en la app que ya usas todos los días.",
    "features.justMessage.title": "Solo escribe",
    "features.justMessage.desc": '"Recuérdame pagar el alquiler el 1" – así de simple.',
    "features.voiceTasks.title": "Tareas de voz",
    "features.voiceTasks.desc": "Envía una nota de voz y la convertiremos en tareas. Habla natural, entendemos.",
    "features.forwardToCreate.title": "Reenviar para crear",
    "features.forwardToCreate.desc": "¿Un mensaje con algo por hacer? Reenvíalo al bot y se convierte en tarea.",
    "features.multipleTasks.title": "Varias tareas a la vez",
    "features.multipleTasks.desc":
      'Di "Comprar leche, llamar a mamá y enviar el informe" – creamos 3 tareas separadas.',
    "features.snoozeComplete.title": "Posponer y completar",
    "features.snoozeComplete.desc": "Toca para posponer un recordatorio o marcarlo como hecho.",
    "features.attachFiles.title": "Adjuntar archivos",
    "features.attachFiles.desc": "Añade fotos, documentos y archivos a tus tareas.",
    "features.languages.title": "29+ idiomas",
    "features.languages.desc": "Habla tu idioma. Español, inglés, hebreo, árabe y muchos más.",
    "features.new": "NUEVO",

    "powerFeatures.badge": "✨ Funciones avanzadas",
    "powerFeatures.title": "Haz más,",
    "powerFeatures.titleHighlight": "más rápido",
    "powerFeatures.subtitle": "Capacidades avanzadas que hacen la gestión de tareas sin esfuerzo.",
    "powerFeatures.voiceToTasks.title": "Voz a tareas",
    "powerFeatures.voiceToTasks.desc":
      "Envía un mensaje de voz con varias tareas. Entendemos y creamos tareas separadas.",
    "powerFeatures.organizeIntoLists.title": "Organizar en listas",
    "powerFeatures.organizeIntoLists.desc": 'Di "Añadir a mi lista de compras" y lo ponemos exactamente donde quieres.',
    "powerFeatures.smartSnoozing.title": "Posposición inteligente",
    "powerFeatures.smartSnoozing.desc": '¿No estás listo? Toca "Posponer 2 horas" o "Recordar mañana a las 9".',
    "powerFeatures.languageSupport.title": "Habla tu idioma",
    "powerFeatures.languageSupport.desc": "Crea tareas en 29+ idiomas. Español, inglés, francés y más.",
    "powerFeatures.moreLanguages": "+17 más",
    "powerFeatures.fileAttachments.title": "Archivos adjuntos",
    "powerFeatures.fileAttachments.desc": "Adjunta fotos, recibos, documentos a cualquier tarea.",
    "powerFeatures.smartListAssignment.title": "Asignación inteligente",
    "powerFeatures.smartListAssignment.desc": '"Añadir huevos a la lista de compras" – sabemos exactamente dónde va.',

    "howItWorks.title": "Cómo",
    "howItWorks.titleHighlight": "funciona",
    "howItWorks.subtitle": "Empieza en segundos. Sin configuración compleja, sin curva de aprendizaje.",
    "howItWorks.step1.title": "Inicia un chat",
    "howItWorks.step1.desc": "Abre WhatsApp y comienza una conversación con el bot de Any.do.",
    "howItWorks.step2.title": "Crea tu tarea",
    "howItWorks.step2.desc": "Escribe, habla o reenvía un mensaje. Añade a cualquier lista.",
    "howItWorks.step3.title": "Recibe recordatorio",
    "howItWorks.step3.desc": "Recibe un mensaje de WhatsApp cuando sea el momento. Toca para posponer o completar.",

    "socialProof.title": "Amado por",
    "socialProof.titleHighlight": "millones",
    "socialProof.subtitle": "Únete a la comunidad que ha transformado su productividad.",
    "socialProof.users": "Usuarios",
    "socialProof.reviews": "Reseñas 5 estrellas",
    "socialProof.appStoreRating": "Calificación App Store",
    "socialProof.countries": "Países",
    "socialProof.review1.text":
      "¡Por fin una app de tareas que encaja en mi vida! Literalmente nunca salgo de WhatsApp, así que es perfecta.",
    "socialProof.review1.author": "Sara M.",
    "socialProof.review1.role": "Gerente de Marketing",
    "socialProof.review2.text": "La función de reenvío es genial. Alguien me envía algo, lo reenvío, listo.",
    "socialProof.review2.author": "Jaime K.",
    "socialProof.review2.role": "Emprendedor",
    "socialProof.review3.text": "Me encanta recibir recordatorios en WhatsApp. ¡Cambió mi vida!",
    "socialProof.review3.author": "María L.",
    "socialProof.review3.role": "Diseñadora Freelance",

    "pricing.badge": "Oferta especial – 50% DESCUENTO",
    "pricing.title": "Empieza a organizarte",
    "pricing.titleHighlight": "hoy",
    "pricing.subtitle": "Acceso premium a un precio inmejorable. Solo por tiempo limitado.",
    "pricing.offerEnds": "Oferta termina en:",
    "pricing.billedAnnually": "Facturado anualmente a $35.88/año",
    "pricing.cta": "Conectar WhatsApp →",
    "pricing.cancelAnytime": "Cancela cuando quieras",
    "pricing.feature.unlimitedTasks": "Tareas y recordatorios ilimitados",
    "pricing.feature.whatsapp": "Integración con WhatsApp",
    "pricing.feature.forward": "Reenviar para crear tareas",
    "pricing.feature.calendar": "Sincronización de calendario",
    "pricing.feature.priority": "Soporte prioritario",
    "pricing.feature.sync": "Sincronización multi-dispositivo",
    "pricing.perMonth": "/mes",

    "faq.title": "Preguntas",
    "faq.titleHighlight": "frecuentes",
    "faq.subtitle": "Todo lo que necesitas saber sobre Any.do en WhatsApp.",
    "faq.q1": "¿Cómo funciona Any.do con WhatsApp?",
    "faq.a1": "Simplemente envía un mensaje a nuestro bot para crear tareas y establecer recordatorios.",
    "faq.q2": "¿Mis datos son privados y seguros?",
    "faq.a2": "Absolutamente. Usamos cifrado de extremo a extremo y nunca compartimos tus datos.",
    "faq.q3": "¿Necesito instalar una app?",
    "faq.a3": "Puedes usar Any.do completamente dentro de WhatsApp. La app ofrece funciones adicionales.",
    "faq.q4": "¿Puedo sincronizar con mi calendario?",
    "faq.a4": "Sí, Any.do se integra con Google Calendar, Outlook y Apple Calendar, pero no está disponible a través de la integración de WhatsApp. Tus tareas y recordatorios se sincronizarán automáticamente en todos tus dispositivos y calendarios.",

    "footer.privacy": "Privacidad",
    "footer.terms": "Términos",
    "footer.support": "Soporte",
    "footer.copyright": "Todos los derechos reservados.",

    "exitIntent.title": "Empieza en segundos",
    "exitIntent.subtitle": 'Solo envía un mensaje como "Recuérdame llamar a Juan hoy a las 5".',
    "exitIntent.benefit1": "Mensajes de voz → tareas instantáneas",
    "exitIntent.benefit2": "Recordatorios inteligentes a tiempo",
    "exitIntent.benefit3": "Funciona en tu WhatsApp",
    "exitIntent.cta": "Conectar WhatsApp →",
    "exitIntent.noCreditCard": "Sin tarjeta de crédito • Cancela cuando quieras",

    mobileCta: "Conectar WhatsApp →",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30",
    "whatsapp.hero.msg2":
      "✅ 2 tareas añadidas:\n\n📞 **Llamar a mamá** - Hoy a las 6:00 PM\n🎁 **Comprar regalo de cumpleaños**",
    "whatsapp.hero.time2": "10:30",
    "whatsapp.hero.msg3": "🔔 **Llamar a mamá** ¡es hora!",
    "whatsapp.hero.time3": "6:00 PM",

    // WhatsApp Demo Messages
    "whatsapp.demo.msg1": "Añade leche y huevos a la lista de compras",
    "whatsapp.demo.time1": "9:15",
    "whatsapp.demo.msg2": "Añadido a Lista de Compras: leche, huevos 🛒",
    "whatsapp.demo.time2": "9:15",
    "whatsapp.demo.msg3": "Recuérdame la reunión a las 2pm",
    "whatsapp.demo.time3": "9:16",
    "whatsapp.demo.msg4": "Recordatorio establecido para hoy a las 14:00 📅",
    "whatsapp.demo.time4": "9:16",

    // Notifications
    "notification.justStarted": "acaba de empezar su prueba",
    "notification.minutesAgo": "min",
    "notification.from": "de",
    "notification.user1.name": "María G.",
    "notification.user1.location": "Madrid",
    "notification.user2.name": "Carlos R.",
    "notification.user2.location": "Barcelona",
    "notification.user3.name": "Laura M.",
    "notification.user3.location": "México DF",
    "notification.user4.name": "Diego S.",
    "notification.user4.location": "Buenos Aires",
    "notification.user5.name": "Ana P.",
    "notification.user5.location": "Bogotá",
    "notification.user6.name": "José L.",
    "notification.user6.location": "Lima",
    "notification.user7.name": "Carmen V.",
    "notification.user7.location": "Santiago",
    "notification.user8.name": "Pablo H.",
    "notification.user8.location": "Valencia",
  },

  fr: {
    "nav.features": "Fonctionnalités",
    "nav.howItWorks": "Comment ça marche",
    "nav.pricing": "Tarifs",
    "nav.faq": "FAQ",
    "nav.cta": "Connecter WhatsApp →",

    "hero.badge": "Rappels WhatsApp",
    "hero.title": "N'oubliez plus jamais",
    "hero.titleHighlight": "une tâche",
    "hero.subtitle":
      "Recevez des rappels directement dans WhatsApp. Plus de changement d'app, plus de tâches oubliées – écrivez et c'est fait.",
    "hero.cta": "Connecter WhatsApp →",
    "hero.socialProof": "personnes ont commencé aujourd'hui",
    "hero.rating": "de 651K avis",

    "features.title": "Gestion des tâches,",
    "features.titleHighlight": "simplifiée",
    "features.subtitle":
      "Tout ce dont vous avez besoin pour rester organisé, dans l'app que vous utilisez chaque jour.",
    "features.justMessage.title": "Écrivez simplement",
    "features.justMessage.desc": '"Rappelle-moi de payer le loyer le 1er" – c\'est aussi simple.',
    "features.voiceTasks.title": "Tâches vocales",
    "features.voiceTasks.desc": "Envoyez une note vocale et nous la transformons en tâches. Parlez naturellement.",
    "features.forwardToCreate.title": "Transférer pour créer",
    "features.forwardToCreate.desc":
      "Un message avec quelque chose à faire? Transférez-le au bot et ça devient une tâche.",
    "features.multipleTasks.title": "Plusieurs tâches à la fois",
    "features.multipleTasks.desc":
      'Dites "Acheter du lait, appeler maman et envoyer le rapport" – nous créons 3 tâches.',
    "features.snoozeComplete.title": "Reporter et terminer",
    "features.snoozeComplete.desc": "Appuyez pour reporter un rappel ou le marquer comme fait.",
    "features.attachFiles.title": "Joindre des fichiers",
    "features.attachFiles.desc": "Ajoutez photos, documents et fichiers à vos tâches.",
    "features.languages.title": "29+ langues",
    "features.languages.desc": "Parlez votre langue. Français, anglais, espagnol, arabe et plus.",
    "features.new": "NOUVEAU",

    "powerFeatures.badge": "✨ Fonctions puissantes",
    "powerFeatures.title": "Faites plus,",
    "powerFeatures.titleHighlight": "plus vite",
    "powerFeatures.subtitle": "Des capacités avancées qui rendent la gestion des tâches sans effort.",
    "powerFeatures.voiceToTasks.title": "Voix vers tâches",
    "powerFeatures.voiceToTasks.desc":
      "Envoyez un message vocal avec plusieurs tâches. Nous comprenons et créons des tâches séparées.",
    "powerFeatures.organizeIntoLists.title": "Organiser en listes",
    "powerFeatures.organizeIntoLists.desc":
      'Dites "Ajouter à ma liste de courses" et nous classons exactement où vous voulez.',
    "powerFeatures.smartSnoozing.title": "Report intelligent",
    "powerFeatures.smartSnoozing.desc": 'Pas prêt? Appuyez "Reporter 2 heures" ou "Rappeler demain à 9h".',
    "powerFeatures.languageSupport.title": "Parlez votre langue",
    "powerFeatures.languageSupport.desc": "Créez des tâches en 29+ langues. Français, anglais, espagnol et plus.",
    "powerFeatures.moreLanguages": "+17 de plus",
    "powerFeatures.fileAttachments.title": "Pièces jointes",
    "powerFeatures.fileAttachments.desc": "Joignez photos, reçus, documents à toute tâche.",
    "powerFeatures.smartListAssignment.title": "Attribution intelligente",
    "powerFeatures.smartListAssignment.desc": '"Ajouter des œufs à ma liste de courses" – nous savons exactement où.',

    "howItWorks.title": "Comment ça",
    "howItWorks.titleHighlight": "marche",
    "howItWorks.subtitle": "Commencez en secondes. Pas de configuration complexe.",
    "howItWorks.step1.title": "Démarrer un chat",
    "howItWorks.step1.desc": "Ouvrez WhatsApp et commencez une conversation avec le bot Any.do.",
    "howItWorks.step2.title": "Créer votre tâche",
    "howItWorks.step2.desc": "Tapez, parlez ou transférez un message. Ajoutez à n'importe quelle liste.",
    "howItWorks.step3.title": "Recevoir un rappel",
    "howItWorks.step3.desc": "Recevez un message WhatsApp quand c'est l'heure. Appuyez pour reporter ou terminer.",

    "socialProof.title": "Adoré par",
    "socialProof.titleHighlight": "des millions",
    "socialProof.subtitle": "Rejoignez la communauté qui a transformé sa productivité.",
    "socialProof.users": "Utilisateurs",
    "socialProof.reviews": "Avis 5 étoiles",
    "socialProof.appStoreRating": "Note App Store",
    "socialProof.countries": "Pays",
    "socialProof.review1.text":
      "Enfin une app de tâches qui s'adapte à ma vie! Je ne quitte jamais WhatsApp, donc c'est parfait.",
    "socialProof.review1.author": "Sarah M.",
    "socialProof.review1.role": "Responsable Marketing",
    "socialProof.review2.text":
      "La fonction de transfert est géniale. On m'envoie quelque chose, je transfère, c'est fait.",
    "socialProof.review2.author": "Jacques K.",
    "socialProof.review2.role": "Entrepreneur",
    "socialProof.review3.text": "J'adore recevoir les rappels dans WhatsApp. Ça a changé ma vie!",
    "socialProof.review3.author": "Marie L.",
    "socialProof.review3.role": "Designer Freelance",

    "pricing.badge": "Offre spéciale – 50% DE RÉDUCTION",
    "pricing.title": "Commencez à vous organiser",
    "pricing.titleHighlight": "aujourd'hui",
    "pricing.subtitle": "Accès premium à un prix imbattable. Offre limitée.",
    "pricing.offerEnds": "Offre se termine dans:",
    "pricing.billedAnnually": "Facturé annuellement à $35.88/an",
    "pricing.cta": "Connecter WhatsApp →",
    "pricing.cancelAnytime": "Annulez à tout moment",
    "pricing.feature.unlimitedTasks": "Tâches et rappels illimités",
    "pricing.feature.whatsapp": "Intégration WhatsApp",
    "pricing.feature.forward": "Transférer pour créer",
    "pricing.feature.calendar": "Synchronisation calendrier",
    "pricing.feature.priority": "Support prioritaire",
    "pricing.feature.sync": "Synchronisation multi-appareils",
    "pricing.perMonth": "/mois",

    "faq.title": "Questions",
    "faq.titleHighlight": "fréquentes",
    "faq.subtitle": "Tout ce que vous devez savoir sur Any.do sur WhatsApp.",
    "faq.q1": "Comment fonctionne Any.do avec WhatsApp?",
    "faq.a1": "Envoyez simplement un message à notre bot pour créer des tâches et définir des rappels.",
    "faq.q2": "Mes données sont-elles privées et sécurisées?",
    "faq.a2": "Absolument. Nous utilisons le chiffrement de bout en bout et ne partageons jamais vos données.",
    "faq.q3": "Dois-je installer une application?",
    "faq.a3": "Vous pouvez utiliser Any.do entièrement dans WhatsApp. L'app offre des fonctionnalités supplémentaires.",
    "faq.q4": "Puis-je synchroniser avec mon calendrier?",
    "faq.a4": "Oui, Any.do s'intègre avec Google Calendar, Outlook et Apple Calendar, mais ce n'est pas disponible via l'intégration WhatsApp. Vos tâches et rappels se synchroniseront automatiquement sur tous vos appareils et calendriers.",

    "footer.privacy": "Confidentialité",
    "footer.terms": "Conditions",
    "footer.support": "Support",
    "footer.copyright": "Tous droits réservés.",

    "exitIntent.title": "Commencez en secondes",
    "exitIntent.subtitle": 'Envoyez simplement un message comme "Rappelle-moi d\'appeler Jean à 17h".',
    "exitIntent.benefit1": "Messages vocaux → tâches instantanées",
    "exitIntent.benefit2": "Rappels intelligents au bon moment",
    "exitIntent.benefit3": "Fonctionne dans votre WhatsApp",
    "exitIntent.cta": "Connecter WhatsApp →",
    "exitIntent.noCreditCard": "Pas de carte de crédit • Annulez à tout moment",

    mobileCta: "Connecter WhatsApp →",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30",
    "whatsapp.hero.msg2":
      "✅ 2 tâches ajoutées :\n\n📞 **Appeler maman** - Aujourd'hui à 18h00\n🎁 **Acheter cadeau d'anniversaire**",
    "whatsapp.hero.time2": "10:30",
    "whatsapp.hero.msg3": "🔔 **Appeler maman** c'est l'heure !",
    "whatsapp.hero.time3": "18:00",

    // WhatsApp Demo Messages
    "whatsapp.demo.msg1": "Ajoute du lait et des œufs à la liste de courses",
    "whatsapp.demo.time1": "9:15",
    "whatsapp.demo.msg2": "Ajouté à la Liste de Courses : lait, œufs 🛒",
    "whatsapp.demo.time2": "9:15",
    "whatsapp.demo.msg3": "Rappelle-moi la réunion à 14h",
    "whatsapp.demo.time3": "9:16",
    "whatsapp.demo.msg4": "Rappel défini pour aujourd'hui à 14h00 📅",
    "whatsapp.demo.time4": "9:16",

    // Notifications
    "notification.justStarted": "vient de commencer son essai",
    "notification.minutesAgo": "min",
    "notification.from": "de",
    "notification.user1.name": "Sophie M.",
    "notification.user1.location": "Paris",
    "notification.user2.name": "Pierre D.",
    "notification.user2.location": "Lyon",
    "notification.user3.name": "Marie L.",
    "notification.user3.location": "Marseille",
    "notification.user4.name": "Thomas R.",
    "notification.user4.location": "Bordeaux",
    "notification.user5.name": "Camille S.",
    "notification.user5.location": "Nice",
    "notification.user6.name": "Antoine B.",
    "notification.user6.location": "Bruxelles",
    "notification.user7.name": "Julie P.",
    "notification.user7.location": "Genève",
    "notification.user8.name": "Nicolas H.",
    "notification.user8.location": "Montréal",
  },

  he: {
    // Header
    "nav.features": "תכונות",
    "nav.howItWorks": "איך זה עובד",
    "nav.pricing": "מחירים",
    "nav.faq": "שאלות נפוצות",
    "nav.cta": "חבר וואטסאפ ←",

    // Hero
    "hero.badge": "תזכורות בוואטסאפ",
    "hero.title": "לא לשכוח",
    "hero.titleHighlight": "אף משימה",
    "hero.subtitle": "קבל תזכורות ישירות בוואטסאפ. בלי להחליף אפליקציות, בלי משימות שנשכחות – פשוט שלח הודעה וזהו.",
    "hero.cta": "חבר וואטסאפ ←",
    "hero.socialProof": "אנשים התחילו היום",
    "hero.rating": "מ-651K ביקורות",

    // Features
    "features.title": "ניהול משימות,",
    "features.titleHighlight": "בפשטות",
    "features.subtitle": "כל מה שצריך כדי להישאר מאורגן, ישירות באפליקציה שאתה משתמש בה כל יום.",
    "features.justMessage.title": "פשוט שלח הודעה",
    "features.justMessage.desc": '"תזכיר לי לשלם שכירות ב-1 לחודש" – פשוט ככה.',
    "features.voiceTasks.title": "משימות קוליות",
    "features.voiceTasks.desc": "שלח הודעה קולית ונהפוך אותה למשימות. דבר בטבעיות, אנחנו מבינים.",
    "features.forwardToCreate.title": "העבר ליצירה",
    "features.forwardToCreate.desc": "קיבלת הודעה עם משהו לעשות? העבר לבוט והיא הופכת למשימה.",
    "features.multipleTasks.title": "כמה משימות בבת אחת",
    "features.multipleTasks.desc": 'אמור "לקנות חלב, להתקשר לאמא, ולשלוח את הדוח" – ניצור 3 משימות נפרדות.',
    "features.snoozeComplete.title": "דחה והשלם",
    "features.snoozeComplete.desc": "לחץ כדי לדחות תזכורת או לסמן כבוצע. שליטה מלאה בלי לעזוב את וואטסאפ.",
    "features.attachFiles.title": "צרף קבצים",
    "features.attachFiles.desc": "הוסף תמונות, מסמכים וקבצים למשימות שלך. הכל במקום אחד.",
    "features.languages.title": "+29 שפות",
    "features.languages.desc": "דבר בשפה שלך. אנחנו תומכים בעברית, אנגלית, ערבית, ספרדית ועוד.",
    "features.new": "חדש",

    // Power Features
    "powerFeatures.badge": "✨ תכונות מתקדמות",
    "powerFeatures.title": "עשה יותר,",
    "powerFeatures.titleHighlight": "מהר יותר",
    "powerFeatures.subtitle": "יכולות מתקדמות שהופכות את ניהול המשימות לקל.",
    "powerFeatures.voiceToTasks.title": "קול למשימות",
    "powerFeatures.voiceToTasks.desc":
      'שלח הודעה קולית עם כמה משימות. "לקנות חלב, לקבוע תור לרופא שיניים, ולשלוח מייל לצוות על פגישת יום שישי" – נבין וניצור משימות נפרדות.',
    "powerFeatures.organizeIntoLists.title": "ארגן ברשימות",
    "powerFeatures.organizeIntoLists.desc":
      'אמור "הוסף לרשימת הקניות" או "שים את זה במשימות עבודה" ונשים בדיוק איפה שאתה רוצה.',
    "powerFeatures.smartSnoozing.title": "דחייה חכמה",
    "powerFeatures.smartSnoozing.desc": 'לא מוכן עכשיו? לחץ "דחה שעתיים" או "תזכיר לי מחר ב-9 בבוקר".',
    "powerFeatures.languageSupport.title": "דבר בשפה שלך",
    "powerFeatures.languageSupport.desc":
      "צור משימות ב-29+ שפות. עברית, אנגלית, ערבית, צרפתית ועוד – אנחנו מבינים את כולן.",
    "powerFeatures.moreLanguages": "+17 נוספות",
    "powerFeatures.fileAttachments.title": "קבצים מצורפים",
    "powerFeatures.fileAttachments.desc": "צרף תמונות, קבלות, מסמכים לכל משימה. פשוט שלח את הקובץ ואמור לאיזו משימה.",
    "powerFeatures.smartListAssignment.title": "הקצאת רשימה חכמה",
    "powerFeatures.smartListAssignment.desc": '"הוסף ביצים לרשימת הקניות" – אנחנו יודעים בדיוק לאן.',

    // How It Works
    "howItWorks.title": "איך זה",
    "howItWorks.titleHighlight": "עובד",
    "howItWorks.subtitle": "התחל בשניות. בלי הגדרות מסובכות, בלי עקומת למידה.",
    "howItWorks.step1.title": "התחל צ'אט",
    "howItWorks.step1.desc": "פתח וואטסאפ והתחל שיחה עם הבוט של Any.do.",
    "howItWorks.step2.title": "צור משימה",
    "howItWorks.step2.desc": "כתוב, דבר או העבר הודעה. הוסף לכל רשימה שתרצה.",
    "howItWorks.step3.title": "קבל תזכורת",
    "howItWorks.step3.desc": "קבל הודעת וואטסאפ כשמגיע הזמן. לחץ לדחות או להשלים.",

    // Social Proof
    "socialProof.title": "אהוב על ידי",
    "socialProof.titleHighlight": "מיליונים",
    "socialProof.subtitle": "הצטרף לקהילה שהפכה את הפרודוקטיביות שלה.",
    "socialProof.users": "משתמשים",
    "socialProof.reviews": "ביקורות 5 כוכבים",
    "socialProof.appStoreRating": "דירוג App Store",
    "socialProof.countries": "מדינות",
    "socialProof.review1.text": "סוף סוף אפליקציית משימות שמתאימה לחיים שלי! אני ממש לא עוזבת את וואטסאפ, אז זה מושלם.",
    "socialProof.review1.author": "שרה מ.",
    "socialProof.review1.role": "מנהלת שיווק",
    "socialProof.review2.text": "התכונה של העברה היא גאונית. מישהו שולח לי משהו לעשות, אני מעביר, נגמר.",
    "socialProof.review2.author": "יעקב כ.",
    "socialProof.review2.role": "יזם",
    "socialProof.review3.text": "אני אוהבת לקבל תזכורות ישר בוואטסאפ. בלי להחליף אפליקציות. שינה לי את החיים!",
    "socialProof.review3.author": "מריה ל.",
    "socialProof.review3.role": "מעצבת עצמאית",

    // Pricing
    "pricing.badge": "מבצע מיוחד – 50% הנחה",
    "pricing.title": "התחל להתארגן",
    "pricing.titleHighlight": "היום",
    "pricing.subtitle": "קבל גישת פרימיום במחיר שאין כמוהו. לזמן מוגבל בלבד.",
    "pricing.offerEnds": "המבצע מסתיים בעוד:",
    "pricing.billedAnnually": "חיוב שנתי של $35.88/שנה",
    "pricing.cta": "חבר וואטסאפ ←",
    "pricing.cancelAnytime": "ביטול בכל עת",
    "pricing.feature.unlimitedTasks": "משימות ותזכורות ללא הגבלה",
    "pricing.feature.whatsapp": "אינטגרציה עם וואטסאפ",
    "pricing.feature.forward": "העבר ליצירת משימות",
    "pricing.feature.calendar": "סנכרון יומן",
    "pricing.feature.priority": "תמיכה עדיפה",
    "pricing.feature.sync": "סנכרון בין מכשירים",
    "pricing.perMonth": "/חודש",

    // FAQ
    "faq.title": "שאלות",
    "faq.titleHighlight": "נפוצות",
    "faq.subtitle": "כל מה שצריך לדעת על Any.do בוואטסאפ.",
    "faq.q1": "איך Any.do עובד עם וואטסאפ?",
    "faq.a1":
      "פשוט שלח הודעה לבוט וואטסאפ שלנו כדי ליצור משימות, להגדיר תזכורות ולנהל את רשימת המשימות. אתה יכול לשלוח הודעות קוליות, להעביר הודעות ואנחנו נהפוך אותן אוטומטית למשימות.",
    "faq.q2": "האם המידע שלי פרטי ומאובטח?",
    "faq.a2": "בהחלט. אנחנו משתמשים בהצפנה מקצה לקצה ולעולם לא משתפים את המידע שלך עם צדדים שלישיים.",
    "faq.q3": "האם אני צריך להתקין אפליקציה?",
    "faq.a3":
      "אתה יכול להשתמש ב-Any.do לגמרי בתוך וואטסאפ בלי להוריד שום דבר. אבל אפליקציית Any.do מספקת יכולות נוספות כמו תצוגות יומן וווידג'טים.",
    "faq.q4": "האם אני יכול לסנכרן עם היומן שלי?",
    "faq.a4": "כן, Any.do משתלב עם Google Calendar, Outlook ו-Apple Calendar אבל זה לא זמין דרך האינטגרציה עם וואטסאפ. המשימות והתזכורות שלך יסתנכרנו אוטומטית בכל המכשירים והיומנים שלך.",

    // Footer
    "footer.privacy": "פרטיות",
    "footer.terms": "תנאים",
    "footer.support": "תמיכה",
    "footer.copyright": "כל הזכויות שמורות.",

    // Exit Intent
    "exitIntent.title": "התחל בשניות",
    "exitIntent.subtitle": 'פשוט שלח הודעה כמו "תזכיר לי להתקשר לג\'ון היום ב-5".',
    "exitIntent.benefit1": "הודעות קוליות ← משימות מיידיות",
    "exitIntent.benefit2": "תזכורות חכמות בזמן הנכון",
    "exitIntent.benefit3": "עובד בוואטסאפ הקיים שלך",
    "exitIntent.cta": "חבר וואטסאפ ←",
    "exitIntent.noCreditCard": "לא צריך כרטיס אשראי • ביטול בכל עת",

    mobileCta: "← חבר וואטסאפ",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30",
    "whatsapp.hero.msg2": "✅ נוספו 2 משימות:\n\n📞 **להתקשר לאמא** - היום ב-18:00\n🎁 **לקנות מתנת יום הולדת**",
    "whatsapp.hero.time2": "10:30",
    "whatsapp.hero.msg3": "🔔 **להתקשר לאמא** הגיע הזמן!",
    "whatsapp.hero.time3": "18:00",

    // WhatsApp Demo Messages
    "whatsapp.demo.msg1": "הוסף חלב וביצים לרשימת הקניות",
    "whatsapp.demo.time1": "9:15",
    "whatsapp.demo.msg2": "נוסף לרשימת הקניות: חלב, ביצים 🛒",
    "whatsapp.demo.time2": "9:15",
    "whatsapp.demo.msg3": "תזכיר לי על הפגישה ב-2 אחה״צ",
    "whatsapp.demo.time3": "9:16",
    "whatsapp.demo.msg4": "תזכורת נקבעה להיום ב-14:00 📅",
    "whatsapp.demo.time4": "9:16",

    // Notifications
    "notification.justStarted": "התחיל/ה את התקופה הניסיונית",
    "notification.minutesAgo": "ד",
    "notification.from": "מ",
    "notification.user1.name": "שרה מ.",
    "notification.user1.location": "תל אביב",
    "notification.user2.name": "יעקב כ.",
    "notification.user2.location": "ירושלים",
    "notification.user3.name": "מיכל ל.",
    "notification.user3.location": "חיפה",
    "notification.user4.name": "דוד ר.",
    "notification.user4.location": "באר שבע",
    "notification.user5.name": "רחל ש.",
    "notification.user5.location": "רמת גן",
    "notification.user6.name": "אבי כ.",
    "notification.user6.location": "פתח תקווה",
    "notification.user7.name": "נועה מ.",
    "notification.user7.location": "נתניה",
    "notification.user8.name": "עמית ב.",
    "notification.user8.location": "הרצליה",
  },

  hi: {
    "nav.features": "सुविधाएं",
    "nav.howItWorks": "कैसे काम करता है",
    "nav.pricing": "मूल्य",
    "nav.faq": "सवाल",
    "nav.cta": "WhatsApp कनेक्ट करें →",

    "hero.badge": "WhatsApp रिमाइंडर",
    "hero.title": "कोई भी टास्क",
    "hero.titleHighlight": "कभी न भूलें",
    "hero.subtitle":
      "WhatsApp में सीधे रिमाइंडर पाएं। कोई ऐप स्विचिंग नहीं, कोई भूली हुई टास्क नहीं – बस मैसेज करें और हो गया।",
    "hero.cta": "WhatsApp कनेक्ट करें →",
    "hero.socialProof": "लोगों ने आज शुरू किया",
    "hero.rating": "651K रिव्यू से",

    "features.title": "टास्क मैनेजमेंट,",
    "features.titleHighlight": "सरल",
    "features.subtitle": "व्यवस्थित रहने के लिए सब कुछ, उसी ऐप में जो आप रोज़ इस्तेमाल करते हैं।",
    "features.justMessage.title": "बस मैसेज करें",
    "features.justMessage.desc": '"1 तारीख को किराया देने की याद दिलाओ" – इतना आसान।',
    "features.voiceTasks.title": "वॉइस टास्क",
    "features.voiceTasks.desc": "वॉइस नोट भेजें और हम टास्क बना देंगे। स्वाभाविक बोलें, हम समझते हैं।",
    "features.forwardToCreate.title": "फॉरवर्ड करके बनाएं",
    "features.forwardToCreate.desc": "कोई मैसेज मिला जिसमें कुछ करना है? बॉट को फॉरवर्ड करें और टास्क बन जाएगी।",
    "features.multipleTasks.title": "एक साथ कई टास्क",
    "features.multipleTasks.desc": '"दूध खरीदो, मम्मी को कॉल करो, रिपोर्ट भेजो" – हम 3 अलग टास्क बनाएंगे।',
    "features.snoozeComplete.title": "स्नूज़ और कंप्लीट",
    "features.snoozeComplete.desc": "रिमाइंडर को स्नूज़ या पूरा करने के लिए टैप करें।",
    "features.attachFiles.title": "फाइल अटैच करें",
    "features.attachFiles.desc": "अपनी टास्क में फोटो, डॉक्यूमेंट और फाइल्स जोड़ें।",
    "features.languages.title": "29+ भाषाएं",
    "features.languages.desc": "अपनी भाषा बोलें। हिंदी, अंग्रेजी, अरबी, स्पेनिश और बहुत कुछ।",
    "features.new": "नया",

    "powerFeatures.badge": "✨ पावर फीचर्स",
    "powerFeatures.title": "ज़्यादा करें,",
    "powerFeatures.titleHighlight": "तेज़",
    "powerFeatures.subtitle": "एडवांस्ड क्षमताएं जो टास्क मैनेजमेंट को आसान बनाती हैं।",
    "powerFeatures.voiceToTasks.title": "आवाज़ से टास्क",
    "powerFeatures.voiceToTasks.desc": "कई टास्क के साथ वॉइस नोट भेजें। हम समझेंगे और अलग-अलग टास्क बनाएंगे।",
    "powerFeatures.organizeIntoLists.title": "लिस्ट में व्यवस्थित करें",
    "powerFeatures.organizeIntoLists.desc": '"शॉपिंग लिस्ट में जोड़ो" कहें और हम वहीं रख देंगे जहां आप चाहते हैं।',
    "powerFeatures.smartSnoozing.title": "स्मार्ट स्नूज़िंग",
    "powerFeatures.smartSnoozing.desc": 'तैयार नहीं? "2 घंटे स्नूज़" या "कल सुबह 9 बजे याद दिलाओ" टैप करें।',
    "powerFeatures.languageSupport.title": "अपनी भाषा बोलें",
    "powerFeatures.languageSupport.desc": "29+ भाषाओं में टास्क बनाएं। हिंदी, अंग्रेजी, फ्रेंच और बहुत कुछ।",
    "powerFeatures.moreLanguages": "+17 और",
    "powerFeatures.fileAttachments.title": "फाइल अटैचमेंट",
    "powerFeatures.fileAttachments.desc": "किसी भी टास्क में फोटो, रसीद, डॉक्यूमेंट जोड़ें।",
    "powerFeatures.smartListAssignment.title": "स्मार्ट लिस्ट असाइनमेंट",
    "powerFeatures.smartListAssignment.desc": '"शॉपिंग लिस्ट में अंडे जोड़ो" – हम जानते हैं कहां जाएगा।',

    "howItWorks.title": "कैसे",
    "howItWorks.titleHighlight": "काम करता है",
    "howItWorks.subtitle": "सेकंड में शुरू करें। कोई जटिल सेटअप नहीं।",
    "howItWorks.step1.title": "चैट शुरू करें",
    "howItWorks.step1.desc": "WhatsApp खोलें और Any.do बॉट के साथ बातचीत शुरू करें।",
    "howItWorks.step2.title": "अपना टास्क बनाएं",
    "howItWorks.step2.desc": "टाइप करें, बोलें या मैसेज फॉरवर्ड करें।",
    "howItWorks.step3.title": "रिमाइंडर पाएं",
    "howItWorks.step3.desc": "समय आने पर WhatsApp मैसेज पाएं। स्नूज़ या कंप्लीट के लिए टैप करें।",

    "socialProof.title": "लाखों लोगों द्वारा",
    "socialProof.titleHighlight": "पसंद",
    "socialProof.subtitle": "उन लोगों के समुदाय में शामिल हों जिन्होंने अपनी उत्पादकता बदल दी।",
    "socialProof.users": "उपयोगकर्ता",
    "socialProof.reviews": "5-स्टार रिव्यू",
    "socialProof.appStoreRating": "ऐप स्टोर रेटिंग",
    "socialProof.countries": "देश",
    "socialProof.review1.text":
      "आखिरकार एक टू-डू ऐप जो मेरी ज़िंदगी में फिट होता है! मैं WhatsApp नहीं छोड़ती, तो यह परफेक्ट है।",
    "socialProof.review1.author": "सारा एम.",
    "socialProof.review1.role": "मार्केटिंग मैनेजर",
    "socialProof.review2.text": "फॉरवर्डिंग फीचर जीनियस है। कोई कुछ भेजता है, मैं फॉरवर्ड करता हूं, हो गया।",
    "socialProof.review2.author": "जेम्स के.",
    "socialProof.review2.role": "उद्यमी",
    "socialProof.review3.text": "WhatsApp में रिमाइंडर पाना बहुत अच्छा है। लाइफ चेंजिंग!",
    "socialProof.review3.author": "मारिया एल.",
    "socialProof.review3.role": "फ्रीलांस डिज़ाइनर",

    "pricing.badge": "विशेष प्रस्ताव – 50% छूट",
    "pricing.title": "आज ही व्यवस्थित होना",
    "pricing.titleHighlight": "शुरू करें",
    "pricing.subtitle": "अविश्वसनीय कीमत पर प्रीमियम एक्सेस पाएं।",
    "pricing.offerEnds": "प्रस्ताव समाप्त:",
    "pricing.billedAnnually": "वार्षिक बिलिंग $35.88/वर्ष",
    "pricing.cta": "WhatsApp कनेक्ट करें →",
    "pricing.cancelAnytime": "कभी भी रद्द करें",
    "pricing.feature.unlimitedTasks": "असीमित टास्क और रिमाइंडर",
    "pricing.feature.whatsapp": "WhatsApp इंटीग्रेशन",
    "pricing.feature.forward": "फॉरवर्ड करके टास्क बनाएं",
    "pricing.feature.calendar": "कैलेंडर सिंक",
    "pricing.feature.priority": "प्राथमिकता सपोर्ट",
    "pricing.feature.sync": "क्रॉस-डिवाइस सिंक",
    "pricing.perMonth": "/महीना",

    "faq.title": "अक्सर पूछे जाने वाले",
    "faq.titleHighlight": "सवाल",
    "faq.subtitle": "WhatsApp पर Any.do के बारे में सब कुछ।",
    "faq.q1": "Any.do WhatsApp के साथ कैसे काम करता है?",
    "faq.a1": "बस हमारे बॉट को मैसेज करें टास्क बनाने और रिमाइंडर सेट करने के लिए।",
    "faq.q2": "क्या मेरा डेटा प्राइवेट और सुरक्षित है?",
    "faq.a2": "बिल्कुल। हम एंड-टू-एंड एन्क्रिप्शन का उपयोग करते हैं।",
    "faq.q3": "क्या मुझे ऐप इंस्टॉल करना होगा?",
    "faq.a3": "आप Any.do को पूरी तरह से WhatsApp में इस्तेमाल कर सकते हैं। ऐप अतिरिक्त फीचर्स देता है।",
    "faq.q4": "क्या मैं अपने कैलेंडर के साथ सिंक कर सकता हूं?",
    "faq.a4": "हां, Any.do Google Calendar, Outlook और Apple Calendar के साथ इंटीग्रेट होता है लेकिन यह WhatsApp इंटीग्रेशन के माध्यम से उपलब्ध नहीं है। आपके टास्क और रिमाइंडर स्वचालित रूप से आपके सभी डिवाइस और कैलेंडर में सिंक हो जाएंगे।",

    "footer.privacy": "गोपनीयता",
    "footer.terms": "शर्तें",
    "footer.support": "सहायता",
    "footer.copyright": "सर्वाधिकार सुरक्षित।",

    "exitIntent.title": "सेकंड में शुरू करें",
    "exitIntent.subtitle": 'बस एक मैसेज भेजें जैसे "मुझे आज 5 बजे जॉन को कॉल करने की याद दिलाओ"।',
    "exitIntent.benefit1": "वॉइस मैसेज → इंस्टेंट टास्क",
    "exitIntent.benefit2": "सही समय पर स्मार्ट रिमाइंडर",
    "exitIntent.benefit3": "आपके WhatsApp में काम करता है",
    "exitIntent.cta": "WhatsApp कनेक्ट करें →",
    "exitIntent.noCreditCard": "क्रेडिट कार्ड नहीं चाहिए • कभी भी रद्द करें",

    mobileCta: "WhatsApp कनेक्ट करें →",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30",
    "whatsapp.hero.msg2":
      "✅ 2 टास्क जोड़े गए:\n\n📞 **मम्मी को कॉल करो** - आज शाम 6:00 बजे\n🎁 **बर्थडे गिफ्ट खरीदो**",
    "whatsapp.hero.time2": "10:30",
    "whatsapp.hero.msg3": "🔔 **मम्मी को कॉल करो** का समय हो गया!",
    "whatsapp.hero.time3": "6:00 PM",

    // WhatsApp Demo Messages
    "whatsapp.demo.msg1": "शॉपिंग लिस्ट में दूध और अंडे जोड़ो",
    "whatsapp.demo.time1": "9:15",
    "whatsapp.demo.msg2": "शॉपिंग लिस्ट में जोड़ा: दूध, अंडे 🛒",
    "whatsapp.demo.time2": "9:15",
    "whatsapp.demo.msg3": "2 बजे मीटिंग की याद दिलाओ",
    "whatsapp.demo.time3": "9:16",
    "whatsapp.demo.msg4": "आज 2:00 PM के लिए रिमाइंडर सेट 📅",
    "whatsapp.demo.time4": "9:16",

    // Notifications
    "notification.justStarted": "ने अभी ट्रायल शुरू किया",
    "notification.minutesAgo": "मिनट",
    "notification.from": "से",
    "notification.user1.name": "प्रिया एम.",
    "notification.user1.location": "मुंबई",
    "notification.user2.name": "राहुल के.",
    "notification.user2.location": "दिल्ली",
    "notification.user3.name": "अंजलि एल.",
    "notification.user3.location": "बैंगलोर",
    "notification.user4.name": "विकास आर.",
    "notification.user4.location": "चेन्नई",
    "notification.user5.name": "नेहा एस.",
    "notification.user5.location": "हैदराबाद",
    "notification.user6.name": "अमित बी.",
    "notification.user6.location": "पुणे",
    "notification.user7.name": "पूजा एम.",
    "notification.user7.location": "कोलकाता",
    "notification.user8.name": "रोहित के.",
    "notification.user8.location": "जयपुर",
  },

  it: {
    "nav.features": "Funzionalità",
    "nav.howItWorks": "Come funziona",
    "nav.pricing": "Prezzi",
    "nav.faq": "FAQ",
    "nav.cta": "Connetti WhatsApp →",

    "hero.badge": "Promemoria WhatsApp",
    "hero.title": "Non dimenticare mai",
    "hero.titleHighlight": "un'attività",
    "hero.subtitle":
      "Ricevi promemoria direttamente su WhatsApp. Niente cambio app, niente attività dimenticate – scrivi e fatto.",
    "hero.cta": "Connetti WhatsApp →",
    "hero.socialProof": "persone hanno iniziato oggi",
    "hero.rating": "da 651K recensioni",

    "features.title": "Gestione attività,",
    "features.titleHighlight": "semplificata",
    "features.subtitle": "Tutto ciò che serve per restare organizzato, nell'app che usi ogni giorno.",
    "features.justMessage.title": "Scrivi e basta",
    "features.justMessage.desc": '"Ricordami di pagare l\'affitto il 1°" – così semplice.',
    "features.voiceTasks.title": "Attività vocali",
    "features.voiceTasks.desc": "Invia una nota vocale e la trasformiamo in attività. Parla naturalmente.",
    "features.forwardToCreate.title": "Inoltra per creare",
    "features.forwardToCreate.desc": "Un messaggio con qualcosa da fare? Inoltralo al bot e diventa un'attività.",
    "features.multipleTasks.title": "Più attività insieme",
    "features.multipleTasks.desc": 'Di\' "Comprare latte, chiamare mamma e inviare il report" – creiamo 3 attività.',
    "features.snoozeComplete.title": "Posticipa e completa",
    "features.snoozeComplete.desc": "Tocca per posticipare un promemoria o segnarlo come fatto.",
    "features.attachFiles.title": "Allega file",
    "features.attachFiles.desc": "Aggiungi foto, documenti e file alle tue attività.",
    "features.languages.title": "29+ lingue",
    "features.languages.desc": "Parla la tua lingua. Italiano, inglese, spagnolo, arabo e altro.",
    "features.new": "NUOVO",

    "powerFeatures.badge": "✨ Funzioni potenti",
    "powerFeatures.title": "Fai di più,",
    "powerFeatures.titleHighlight": "più veloce",
    "powerFeatures.subtitle": "Capacità avanzate che rendono la gestione delle attività senza sforzo.",
    "powerFeatures.voiceToTasks.title": "Voce ad attività",
    "powerFeatures.voiceToTasks.desc":
      "Invia un messaggio vocale con più attività. Capiamo e creiamo attività separate.",
    "powerFeatures.organizeIntoLists.title": "Organizza in liste",
    "powerFeatures.organizeIntoLists.desc": 'Di\' "Aggiungi alla lista della spesa" e archiviamo dove vuoi.',
    "powerFeatures.smartSnoozing.title": "Posticipo intelligente",
    "powerFeatures.smartSnoozing.desc": 'Non pronto? Tocca "Posticipa 2 ore" o "Ricorda domani alle 9".',
    "powerFeatures.languageSupport.title": "Parla la tua lingua",
    "powerFeatures.languageSupport.desc": "Crea attività in 29+ lingue. Italiano, inglese, francese e altro.",
    "powerFeatures.moreLanguages": "+17 altre",
    "powerFeatures.fileAttachments.title": "Allegati file",
    "powerFeatures.fileAttachments.desc": "Allega foto, ricevute, documenti a qualsiasi attività.",
    "powerFeatures.smartListAssignment.title": "Assegnazione intelligente",
    "powerFeatures.smartListAssignment.desc": '"Aggiungi uova alla lista della spesa" – sappiamo dove va.',

    "howItWorks.title": "Come",
    "howItWorks.titleHighlight": "funziona",
    "howItWorks.subtitle": "Inizia in secondi. Nessuna configurazione complessa.",
    "howItWorks.step1.title": "Avvia una chat",
    "howItWorks.step1.desc": "Apri WhatsApp e inizia una conversazione con il bot Any.do.",
    "howItWorks.step2.title": "Crea la tua attività",
    "howItWorks.step2.desc": "Digita, parla o inoltra un messaggio. Aggiungi a qualsiasi lista.",
    "howItWorks.step3.title": "Ricevi promemoria",
    "howItWorks.step3.desc": "Ricevi un messaggio WhatsApp quando è ora. Tocca per posticipare o completare.",

    "socialProof.title": "Amato da",
    "socialProof.titleHighlight": "milioni",
    "socialProof.subtitle": "Unisciti alla community che ha trasformato la propria produttività.",
    "socialProof.users": "Utenti",
    "socialProof.reviews": "Recensioni 5 stelle",
    "socialProof.appStoreRating": "Valutazione App Store",
    "socialProof.countries": "Paesi",
    "socialProof.review1.text":
      "Finalmente un'app per attività che si adatta alla mia vita! Non esco mai da WhatsApp, quindi è perfetta.",
    "socialProof.review1.author": "Sara M.",
    "socialProof.review1.role": "Marketing Manager",
    "socialProof.review2.text": "La funzione di inoltro è geniale. Qualcuno mi manda qualcosa, inoltro, fatto.",
    "socialProof.review2.author": "Giacomo K.",
    "socialProof.review2.role": "Imprenditore",
    "socialProof.review3.text": "Adoro ricevere promemoria su WhatsApp. Ha cambiato la mia vita!",
    "socialProof.review3.author": "Maria L.",
    "socialProof.review3.role": "Designer Freelance",

    "pricing.badge": "Offerta speciale – 50% DI SCONTO",
    "pricing.title": "Inizia a organizzarti",
    "pricing.titleHighlight": "oggi",
    "pricing.subtitle": "Accesso premium a un prezzo imbattibile. Solo per tempo limitato.",
    "pricing.offerEnds": "Offerta termina tra:",
    "pricing.billedAnnually": "Fatturato annualmente a $35.88/anno",
    "pricing.cta": "Connetti WhatsApp →",
    "pricing.cancelAnytime": "Cancella quando vuoi",
    "pricing.feature.unlimitedTasks": "Attività e promemoria illimitati",
    "pricing.feature.whatsapp": "Integrazione WhatsApp",
    "pricing.feature.forward": "Inoltra per creare",
    "pricing.feature.calendar": "Sincronizzazione calendario",
    "pricing.feature.priority": "Supporto prioritario",
    "pricing.feature.sync": "Sincronizzazione multi-dispositivo",
    "pricing.perMonth": "/mese",

    "faq.title": "Domande",
    "faq.titleHighlight": "frequenti",
    "faq.subtitle": "Tutto quello che devi sapere su Any.do su WhatsApp.",
    "faq.q1": "Come funziona Any.do con WhatsApp?",
    "faq.a1": "Invia semplicemente un messaggio al nostro bot per creare attività e impostare promemoria.",
    "faq.q2": "I miei dati sono privati e sicuri?",
    "faq.a2": "Assolutamente. Utilizziamo la crittografia end-to-end e non condividiamo mai i tuoi dati.",
    "faq.q3": "Devo installare un'app?",
    "faq.a3": "Puoi usare Any.do interamente su WhatsApp. L'app offre funzionalità aggiuntive.",
    "faq.q4": "Posso sincronizzare con il mio calendario?",
    "faq.a4": "Sì, Any.do si integra con Google Calendar, Outlook e Apple Calendar ma non è disponibile tramite l'integrazione WhatsApp. Le tue attività e i promemoria si sincronizzeranno automaticamente su tutti i tuoi dispositivi e calendari.",

    "footer.privacy": "Privacy",
    "footer.terms": "Termini",
    "footer.support": "Supporto",
    "footer.copyright": "Tutti i diritti riservati.",

    "exitIntent.title": "Inizia in secondi",
    "exitIntent.subtitle": 'Invia un messaggio come "Ricordami di chiamare Giovanni alle 17".',
    "exitIntent.benefit1": "Messaggi vocali → attività istantanee",
    "exitIntent.benefit2": "Promemoria intelligenti al momento giusto",
    "exitIntent.benefit3": "Funziona nel tuo WhatsApp",
    "exitIntent.cta": "Connetti WhatsApp →",
    "exitIntent.noCreditCard": "Nessuna carta di credito • Cancella quando vuoi",

    mobileCta: "Connetti WhatsApp →",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30",
    "whatsapp.hero.msg2":
      "✅ 2 attività aggiunte:\n\n📞 **Chiamare mamma** - Oggi alle 18:00\n🎁 **Comprare regalo di compleanno**",
    "whatsapp.hero.time2": "10:30",
    "whatsapp.hero.msg3": "🔔 **Chiamare mamma** è ora!",
    "whatsapp.hero.time3": "18:00",

    // WhatsApp Demo Messages
    "whatsapp.demo.msg1": "Aggiungi latte e uova alla lista della spesa",
    "whatsapp.demo.time1": "9:15",
    "whatsapp.demo.msg2": "Aggiunto alla Lista della Spesa: latte, uova 🛒",
    "whatsapp.demo.time2": "9:15",
    "whatsapp.demo.msg3": "Ricordami la riunione alle 14",
    "whatsapp.demo.time3": "9:16",
    "whatsapp.demo.msg4": "Promemoria impostato per oggi alle 14:00 📅",
    "whatsapp.demo.time4": "9:16",

    // Notifications
    "notification.justStarted": "ha appena iniziato la prova",
    "notification.minutesAgo": "min",
    "notification.from": "da",
    "notification.user1.name": "Sara M.",
    "notification.user1.location": "Roma",
    "notification.user2.name": "Marco K.",
    "notification.user2.location": "Milano",
    "notification.user3.name": "Giulia L.",
    "notification.user3.location": "Napoli",
    "notification.user4.name": "Luca R.",
    "notification.user4.location": "Torino",
    "notification.user5.name": "Francesca S.",
    "notification.user5.location": "Firenze",
    "notification.user6.name": "Andrea B.",
    "notification.user6.location": "Bologna",
    "notification.user7.name": "Chiara P.",
    "notification.user7.location": "Venezia",
    "notification.user8.name": "Matteo H.",
    "notification.user8.location": "Palermo",
  },

  pt: {
    "nav.features": "Recursos",
    "nav.howItWorks": "Como funciona",
    "nav.pricing": "Preços",
    "nav.faq": "Perguntas",
    "nav.cta": "Conectar WhatsApp →",

    "hero.badge": "Lembretes do WhatsApp",
    "hero.title": "Nunca esqueça",
    "hero.titleHighlight": "uma tarefa",
    "hero.subtitle":
      "Receba lembretes diretamente no WhatsApp. Sem trocar de app, sem tarefas esquecidas – só escreva e pronto.",
    "hero.cta": "Conectar WhatsApp →",
    "hero.socialProof": "pessoas começaram hoje",
    "hero.rating": "de 651K avaliações",

    "features.title": "Gestão de tarefas,",
    "features.titleHighlight": "simplificada",
    "features.subtitle": "Tudo que você precisa para se manter organizado, no app que você já usa todo dia.",
    "features.justMessage.title": "Só escreva",
    "features.justMessage.desc": '"Me lembre de pagar o aluguel no dia 1" – simples assim.',
    "features.voiceTasks.title": "Tarefas por voz",
    "features.voiceTasks.desc": "Envie uma mensagem de voz e transformamos em tarefas. Fale naturalmente.",
    "features.forwardToCreate.title": "Encaminhe para criar",
    "features.forwardToCreate.desc": "Recebeu uma mensagem com algo a fazer? Encaminhe para o bot e vira tarefa.",
    "features.multipleTasks.title": "Várias tarefas de uma vez",
    "features.multipleTasks.desc": 'Diga "Comprar leite, ligar pra mãe e enviar o relatório" – criamos 3 tarefas.',
    "features.snoozeComplete.title": "Adiar e completar",
    "features.snoozeComplete.desc": "Toque para adiar um lembrete ou marcar como feito.",
    "features.attachFiles.title": "Anexar arquivos",
    "features.attachFiles.desc": "Adicione fotos, documentos e arquivos às suas tarefas.",
    "features.languages.title": "29+ idiomas",
    "features.languages.desc": "Fale seu idioma. Português, inglês, espanhol, árabe e muito mais.",
    "features.new": "NOVO",

    "powerFeatures.badge": "✨ Recursos avançados",
    "powerFeatures.title": "Faça mais,",
    "powerFeatures.titleHighlight": "mais rápido",
    "powerFeatures.subtitle": "Capacidades avançadas que tornam a gestão de tarefas fácil.",
    "powerFeatures.voiceToTasks.title": "Voz para tarefas",
    "powerFeatures.voiceToTasks.desc":
      "Envie uma mensagem de voz com várias tarefas. Entendemos e criamos tarefas separadas.",
    "powerFeatures.organizeIntoLists.title": "Organize em listas",
    "powerFeatures.organizeIntoLists.desc":
      'Diga "Adicionar à lista de compras" e colocamos exatamente onde você quer.',
    "powerFeatures.smartSnoozing.title": "Adiamento inteligente",
    "powerFeatures.smartSnoozing.desc": 'Não está pronto? Toque "Adiar 2 horas" ou "Lembrar amanhã às 9".',
    "powerFeatures.languageSupport.title": "Fale seu idioma",
    "powerFeatures.languageSupport.desc": "Crie tarefas em 29+ idiomas. Português, inglês, francês e mais.",
    "powerFeatures.moreLanguages": "+17 mais",
    "powerFeatures.fileAttachments.title": "Anexos de arquivos",
    "powerFeatures.fileAttachments.desc": "Anexe fotos, recibos, documentos a qualquer tarefa.",
    "powerFeatures.smartListAssignment.title": "Atribuição inteligente",
    "powerFeatures.smartListAssignment.desc": '"Adicionar ovos na lista de compras" – sabemos onde vai.',

    "howItWorks.title": "Como",
    "howItWorks.titleHighlight": "funciona",
    "howItWorks.subtitle": "Comece em segundos. Sem configuração complexa.",
    "howItWorks.step1.title": "Inicie um chat",
    "howItWorks.step1.desc": "Abra o WhatsApp e comece uma conversa com o bot do Any.do.",
    "howItWorks.step2.title": "Crie sua tarefa",
    "howItWorks.step2.desc": "Digite, fale ou encaminhe uma mensagem. Adicione a qualquer lista.",
    "howItWorks.step3.title": "Receba lembrete",
    "howItWorks.step3.desc": "Receba uma mensagem do WhatsApp na hora certa. Toque para adiar ou completar.",

    "socialProof.title": "Amado por",
    "socialProof.titleHighlight": "milhões",
    "socialProof.subtitle": "Junte-se à comunidade que transformou sua produtividade.",
    "socialProof.users": "Usuários",
    "socialProof.reviews": "Avaliações 5 estrelas",
    "socialProof.appStoreRating": "Nota na App Store",
    "socialProof.countries": "Países",
    "socialProof.review1.text":
      "Finalmente um app de tarefas que cabe na minha vida! Eu nunca saio do WhatsApp, então é perfeito.",
    "socialProof.review1.author": "Sara M.",
    "socialProof.review1.role": "Gerente de Marketing",
    "socialProof.review2.text": "O recurso de encaminhamento é genial. Alguém me manda algo, eu encaminho, pronto.",
    "socialProof.review2.author": "Tiago K.",
    "socialProof.review2.role": "Empreendedor",
    "socialProof.review3.text": "Adoro receber lembretes no WhatsApp. Mudou minha vida!",
    "socialProof.review3.author": "Maria L.",
    "socialProof.review3.role": "Designer Freelancer",

    "pricing.badge": "Oferta especial – 50% DE DESCONTO",
    "pricing.title": "Comece a se organizar",
    "pricing.titleHighlight": "hoje",
    "pricing.subtitle": "Acesso premium a um preço imbatível. Por tempo limitado.",
    "pricing.offerEnds": "Oferta termina em:",
    "pricing.billedAnnually": "Cobrado anualmente a $35.88/ano",
    "pricing.cta": "Conectar WhatsApp →",
    "pricing.cancelAnytime": "Cancele quando quiser",
    "pricing.feature.unlimitedTasks": "Tarefas e lembretes ilimitados",
    "pricing.feature.whatsapp": "Integração com WhatsApp",
    "pricing.feature.forward": "Encaminhar para criar",
    "pricing.feature.calendar": "Sincronização de calendário",
    "pricing.feature.priority": "Suporte prioritário",
    "pricing.feature.sync": "Sincronização entre dispositivos",
    "pricing.perMonth": "/mês",

    "faq.title": "Perguntas",
    "faq.titleHighlight": "frequentes",
    "faq.subtitle": "Tudo que você precisa saber sobre o Any.do no WhatsApp.",
    "faq.q1": "Como o Any.do funciona com o WhatsApp?",
    "faq.a1": "Basta enviar uma mensagem para nosso bot para criar tarefas e definir lembretes.",
    "faq.q2": "Meus dados são privados e seguros?",
    "faq.a2": "Absolutamente. Usamos criptografia de ponta a ponta e nunca compartilhamos seus dados.",
    "faq.q3": "Preciso instalar um app?",
    "faq.a3": "Você pode usar o Any.do inteiramente no WhatsApp. O app oferece recursos adicionais.",
    "faq.q4": "Posso sincronizar com meu calendário?",
    "faq.a4": "Sim, o Any.do se integra com Google Calendar, Outlook e Apple Calendar, mas não está disponível através da integração com WhatsApp. Suas tarefas e lembretes serão sincronizados automaticamente em todos os seus dispositivos e calendários.",

    "footer.privacy": "Privacidade",
    "footer.terms": "Termos",
    "footer.support": "Suporte",
    "footer.copyright": "Todos os direitos reservados.",

    "exitIntent.title": "Comece em segundos",
    "exitIntent.subtitle": 'Basta enviar uma mensagem como "Me lembre de ligar pro João às 17h".',
    "exitIntent.benefit1": "Mensagens de voz → tarefas instantâneas",
    "exitIntent.benefit2": "Lembretes inteligentes na hora certa",
    "exitIntent.benefit3": "Funciona no seu WhatsApp",
    "exitIntent.cta": "Conectar WhatsApp →",
    "exitIntent.noCreditCard": "Sem cartão de crédito • Cancele quando quiser",

    mobileCta: "Conectar WhatsApp →",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30",
    "whatsapp.hero.msg2":
      "✅ 2 tarefas adicionadas:\n\n📞 **Ligar pra mãe** - Hoje às 18:00\n🎁 **Comprar presente de aniversário**",
    "whatsapp.hero.time2": "10:30",
    "whatsapp.hero.msg3": "🔔 **Ligar pra mãe** é agora!",
    "whatsapp.hero.time3": "18:00",

    // WhatsApp Demo Messages
    "whatsapp.demo.msg1": "Adiciona leite e ovos na lista de compras",
    "whatsapp.demo.time1": "9:15",
    "whatsapp.demo.msg2": "Adicionado à Lista de Compras: leite, ovos 🛒",
    "whatsapp.demo.time2": "9:15",
    "whatsapp.demo.msg3": "Me lembre da reunião às 14h",
    "whatsapp.demo.time3": "9:16",
    "whatsapp.demo.msg4": "Lembrete definido para hoje às 14:00 📅",
    "whatsapp.demo.time4": "9:16",

    // Notifications
    "notification.justStarted": "acabou de começar a avaliação",
    "notification.minutesAgo": "min",
    "notification.from": "de",
    "notification.user1.name": "Ana M.",
    "notification.user1.location": "São Paulo",
    "notification.user2.name": "Pedro K.",
    "notification.user2.location": "Rio de Janeiro",
    "notification.user3.name": "Mariana L.",
    "notification.user3.location": "Lisboa",
    "notification.user4.name": "Lucas R.",
    "notification.user4.location": "Porto",
    "notification.user5.name": "Beatriz S.",
    "notification.user5.location": "Brasília",
    "notification.user6.name": "Gabriel B.",
    "notification.user6.location": "Curitiba",
    "notification.user7.name": "Camila P.",
    "notification.user7.location": "Salvador",
    "notification.user8.name": "Rafael H.",
    "notification.user8.location": "Belo Horizonte",
  },

  ru: {
    "nav.features": "Функции",
    "nav.howItWorks": "Как это работает",
    "nav.pricing": "Цены",
    "nav.faq": "Вопросы",
    "nav.cta": "Подключить WhatsApp →",

    "hero.badge": "Напоминания в WhatsApp",
    "hero.title": "Никогда не забывайте",
    "hero.titleHighlight": "задачи",
    "hero.subtitle":
      "Получайте напоминания прямо в WhatsApp. Без переключения приложений, без забытых задач – просто напишите и готово.",
    "hero.cta": "Подключить WhatsApp →",
    "hero.socialProof": "человек начали сегодня",
    "hero.rating": "из 651K отзывов",

    "features.title": "Управление задачами,",
    "features.titleHighlight": "упрощённое",
    "features.subtitle": "Всё, что нужно для организации, прямо в приложении, которым вы пользуетесь каждый день.",
    "features.justMessage.title": "Просто напишите",
    "features.justMessage.desc": '"Напомни оплатить аренду 1-го числа" – так просто.',
    "features.voiceTasks.title": "Голосовые задачи",
    "features.voiceTasks.desc": "Отправьте голосовое сообщение и мы создадим задачи. Говорите естественно.",
    "features.forwardToCreate.title": "Переслать для создания",
    "features.forwardToCreate.desc": "Получили сообщение с делом? Перешлите боту и оно станет задачей.",
    "features.multipleTasks.title": "Несколько задач сразу",
    "features.multipleTasks.desc": 'Скажите "Купить молоко, позвонить маме и отправить отчёт" – создадим 3 задачи.',
    "features.snoozeComplete.title": "Отложить и завершить",
    "features.snoozeComplete.desc": "Нажмите, чтобы отложить напоминание или отметить выполненным.",
    "features.attachFiles.title": "Прикрепить файлы",
    "features.attachFiles.desc": "Добавляйте фото, документы и файлы к задачам.",
    "features.languages.title": "29+ языков",
    "features.languages.desc": "Говорите на своём языке. Русский, английский, испанский, арабский и многие другие.",
    "features.new": "НОВОЕ",

    "powerFeatures.badge": "✨ Мощные функции",
    "powerFeatures.title": "Делайте больше,",
    "powerFeatures.titleHighlight": "быстрее",
    "powerFeatures.subtitle": "Продвинутые возможности, которые делают управление задачами лёгким.",
    "powerFeatures.voiceToTasks.title": "Голос в задачи",
    "powerFeatures.voiceToTasks.desc":
      "Отправьте голосовое сообщение с несколькими задачами. Мы поймём и создадим отдельные задачи.",
    "powerFeatures.organizeIntoLists.title": "Организуйте в списки",
    "powerFeatures.organizeIntoLists.desc": 'Скажите "Добавить в список покупок" и мы поместим именно туда.',
    "powerFeatures.smartSnoozing.title": "Умное откладывание",
    "powerFeatures.smartSnoozing.desc": 'Не готовы? Нажмите "Отложить на 2 часа" или "Напомнить завтра в 9".',
    "powerFeatures.languageSupport.title": "Говорите на своём языке",
    "powerFeatures.languageSupport.desc": "Создавайте задачи на 29+ языках. Русский, английский, французский и другие.",
    "powerFeatures.moreLanguages": "+17 ещё",
    "powerFeatures.fileAttachments.title": "Прикрепление файлов",
    "powerFeatures.fileAttachments.desc": "Прикрепляйте фото, чеки, документы к любой задаче.",
    "powerFeatures.smartListAssignment.title": "Умное назначение списков",
    "powerFeatures.smartListAssignment.desc": '"Добавить яйца в список покупок" – мы знаем куда.',

    "howItWorks.title": "Как это",
    "howItWorks.titleHighlight": "работает",
    "howItWorks.subtitle": "Начните за секунды. Без сложной настройки.",
    "howItWorks.step1.title": "Начните чат",
    "howItWorks.step1.desc": "Откройте WhatsApp и начните разговор с ботом Any.do.",
    "howItWorks.step2.title": "Создайте задачу",
    "howItWorks.step2.desc": "Напишите, скажите голосом или перешлите сообщение.",
    "howItWorks.step3.title": "Получите напоминание",
    "howItWorks.step3.desc": "Получите сообщение WhatsApp, когда придёт время. Нажмите, чтобы отложить или завершить.",

    "socialProof.title": "Любим",
    "socialProof.titleHighlight": "миллионами",
    "socialProof.subtitle": "Присоединяйтесь к сообществу, которое преобразило свою продуктивность.",
    "socialProof.users": "Пользователей",
    "socialProof.reviews": "5-звёздочных отзывов",
    "socialProof.appStoreRating": "Рейтинг App Store",
    "socialProof.countries": "Стран",
    "socialProof.review1.text":
      "Наконец-то приложение для задач, которое вписывается в мою жизнь! Я никогда не выхожу из WhatsApp, так что это идеально.",
    "socialProof.review1.author": "Сара М.",
    "socialProof.review1.role": "Маркетинг-менеджер",
    "socialProof.review2.text": "Функция пересылки гениальна. Кто-то присылает что-то сделать, я пересылаю, готово.",
    "socialProof.review2.author": "Джеймс К.",
    "socialProof.review2.role": "Предприниматель",
    "socialProof.review3.text": "Обожаю получать напоминания прямо в WhatsApp. Это изменило мою жизнь!",
    "socialProof.review3.author": "Мария Л.",
    "socialProof.review3.role": "Фриланс-дизайнер",

    "pricing.badge": "Специальное предложение – СКИДКА 50%",
    "pricing.title": "Начните организовываться",
    "pricing.titleHighlight": "сегодня",
    "pricing.subtitle": "Премиум-доступ по непревзойдённой цене. Ограниченное время.",
    "pricing.offerEnds": "Предложение заканчивается:",
    "pricing.billedAnnually": "Годовая оплата $35.88/год",
    "pricing.cta": "Подключить WhatsApp →",
    "pricing.cancelAnytime": "Отмена в любое время",
    "pricing.feature.unlimitedTasks": "Безлимитные задачи и напоминания",
    "pricing.feature.whatsapp": "Интеграция с WhatsApp",
    "pricing.feature.forward": "Пересылка для создания задач",
    "pricing.feature.calendar": "Синхронизация календаря",
    "pricing.feature.priority": "Приоритетная поддержка",
    "pricing.feature.sync": "Синхронизация между устройствами",
    "pricing.perMonth": "/мес",

    "faq.title": "Часто задаваемые",
    "faq.titleHighlight": "вопросы",
    "faq.subtitle": "Всё, что нужно знать о Any.do в WhatsApp.",
    "faq.q1": "Как Any.do работает с WhatsApp?",
    "faq.a1": "Просто отправьте сообщение нашему боту, чтобы создать задачи и установить напоминания.",
    "faq.q2": "Мои данные приватны и защищены?",
    "faq.a2": "Абсолютно. Мы используем сквозное шифрование и никогда не делимся вашими данными.",
    "faq.q3": "Нужно ли устанавливать приложение?",
    "faq.a3": "Вы можете использовать Any.do полностью в WhatsApp. Приложение предлагает дополнительные функции.",
    "faq.q4": "Могу ли я синхронизировать с календарём?",
    "faq.a4": "Да, Any.do интегрируется с Google Calendar, Outlook и Apple Calendar, но это недоступно через интеграцию с WhatsApp. Ваши задачи и напоминания будут автоматически синхронизироваться на всех ваших устройствах и календарях.",

    "footer.privacy": "Конфиденциальность",
    "footer.terms": "Условия",
    "footer.support": "Поддержка",
    "footer.copyright": "Все права защищены.",

    "exitIntent.title": "Начните за секунды",
    "exitIntent.subtitle": 'Просто отправьте сообщение вроде "Напомни позвонить Ивану в 17:00".',
    "exitIntent.benefit1": "Голосовые сообщения → мгновенные задачи",
    "exitIntent.benefit2": "Умные напоминания вовремя",
    "exitIntent.benefit3": "Работает в вашем WhatsApp",
    "exitIntent.cta": "Подключить WhatsApp →",
    "exitIntent.noCreditCard": "Без кредитной карты • Отмена в любое время",

    mobileCta: "Подключить WhatsApp →",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30",
    "whatsapp.hero.msg2":
      "✅ Добавлено 2 задачи:\n\n📞 **Позвонить маме** - Сегодня в 18:00\n🎁 **Купить подарок на день рождения**",
    "whatsapp.hero.time2": "10:30",
    "whatsapp.hero.msg3": "🔔 **Позвонить маме** пора!",
    "whatsapp.hero.time3": "18:00",

    // WhatsApp Demo Messages
    "whatsapp.demo.msg1": "Добавь молоко и яйца в список покупок",
    "whatsapp.demo.time1": "9:15",
    "whatsapp.demo.msg2": "Добавлено в Список покупок: молоко, яйца 🛒",
    "whatsapp.demo.time2": "9:15",
    "whatsapp.demo.msg3": "Напомни о встрече в 14:00",
    "whatsapp.demo.time3": "9:16",
    "whatsapp.demo.msg4": "Напоминание установлено на сегодня в 14:00 📅",
    "whatsapp.demo.time4": "9:16",

    // Notifications
    "notification.justStarted": "только что начал пробный период",
    "notification.minutesAgo": "мин",
    "notification.from": "из",
    "notification.user1.name": "Анна М.",
    "notification.user1.location": "Москва",
    "notification.user2.name": "Дмитрий К.",
    "notification.user2.location": "Санкт-Петербург",
    "notification.user3.name": "Мария Л.",
    "notification.user3.location": "Киев",
    "notification.user4.name": "Алексей Р.",
    "notification.user4.location": "Минск",
    "notification.user5.name": "Елена С.",
    "notification.user5.location": "Екатеринбург",
    "notification.user6.name": "Сергей Б.",
    "notification.user6.location": "Новосибирск",
    "notification.user7.name": "Наталья П.",
    "notification.user7.location": "Казань",
    "notification.user8.name": "Иван Х.",
    "notification.user8.location": "Нижний Новгород",
  },

  id: {
    // Header
    "nav.features": "Fitur",
    "nav.howItWorks": "Cara Kerja",
    "nav.pricing": "Harga",
    "nav.faq": "FAQ",
    "nav.cta": "Hubungkan WhatsApp →",

    // Hero
    "hero.badge": "Pengingat WhatsApp",
    "hero.title": "Jangan Pernah Lupa",
    "hero.titleHighlight": "Tugas Lagi",
    "hero.subtitle": "Dapatkan pengingat langsung di WhatsApp. Tidak perlu ganti aplikasi, tidak ada tugas terlupa – cukup kirim pesan dan selesai.",
    "hero.cta": "Hubungkan WhatsApp →",
    "hero.socialProof": "orang mulai hari ini",
    "hero.rating": "dari 651K ulasan",

    // Features
    "features.title": "Manajemen Tugas,",
    "features.titleHighlight": "Disederhanakan",
    "features.subtitle": "Semua yang Anda butuhkan untuk tetap teratur, langsung di aplikasi yang Anda gunakan setiap hari.",
    "features.justMessage.title": "Cukup Kirim Pesan",
    "features.justMessage.desc": '"Ingatkan saya bayar sewa tanggal 1" – sesimpel itu.',
    "features.voiceTasks.title": "Tugas Suara",
    "features.voiceTasks.desc": "Kirim pesan suara dan kami akan mengubahnya menjadi tugas. Bicara alami, kami mengerti.",
    "features.forwardToCreate.title": "Teruskan untuk Membuat",
    "features.forwardToCreate.desc": "Dapat pesan dengan sesuatu yang harus dilakukan? Teruskan ke bot dan jadilah tugas.",
    "features.multipleTasks.title": "Beberapa Tugas Sekaligus",
    "features.multipleTasks.desc": 'Katakan "Beli susu, telepon ibu, dan kirim laporan" – kami akan membuat 3 tugas terpisah.',
    "features.snoozeComplete.title": "Tunda & Selesaikan",
    "features.snoozeComplete.desc": "Ketuk untuk menunda pengingat atau tandai selesai. Tetap terkendali tanpa meninggalkan WhatsApp.",
    "features.attachFiles.title": "Lampirkan File",
    "features.attachFiles.desc": "Tambahkan foto, dokumen, dan file ke tugas Anda. Simpan semuanya di satu tempat.",
    "features.languages.title": "29+ Bahasa",
    "features.languages.desc": "Bicara dalam bahasa Anda. Kami mendukung Indonesia, Inggris, Arab, dan banyak lagi.",
    "features.new": "BARU",

    // Power Features
    "powerFeatures.badge": "✨ Fitur Canggih",
    "powerFeatures.title": "Lakukan Lebih,",
    "powerFeatures.titleHighlight": "Lebih Cepat",
    "powerFeatures.subtitle": "Kemampuan canggih yang membuat manajemen tugas terasa mudah.",
    "powerFeatures.voiceToTasks.title": "Suara ke Tugas",
    "powerFeatures.voiceToTasks.desc": 'Kirim pesan suara dengan beberapa tugas. "Beli susu, jadwalkan dokter gigi, dan email tim tentang rapat Jumat" – kami akan mengerti dan membuat tugas terpisah.',
    "powerFeatures.organizeIntoLists.title": "Organisir ke Daftar",
    "powerFeatures.organizeIntoLists.desc": 'Katakan "Tambah ke daftar belanja" atau "Masukkan ke tugas kerja" dan kami akan simpan persis di tempat yang Anda mau.',
    "powerFeatures.smartSnoozing.title": "Penundaan Pintar",
    "powerFeatures.smartSnoozing.desc": 'Belum siap? Ketuk "Tunda 2 jam" atau "Ingatkan besok jam 9 pagi". Tandai tugas selesai dengan satu ketukan.',
    "powerFeatures.languageSupport.title": "Bicara Bahasa Anda",
    "powerFeatures.languageSupport.desc": "Buat tugas dalam 29+ bahasa. Indonesia, Inggris, Arab, Prancis, dan lainnya – kami mengerti semuanya.",
    "powerFeatures.moreLanguages": "+17 lagi",
    "powerFeatures.fileAttachments.title": "Lampiran File",
    "powerFeatures.fileAttachments.desc": "Lampirkan foto, struk, dokumen ke tugas apa pun. Cukup kirim file dan beri tahu kami tugas mana.",
    "powerFeatures.smartListAssignment.title": "Penugasan Daftar Pintar",
    "powerFeatures.smartListAssignment.desc": '"Tambah telur ke daftar belanja" – kami tahu persis ke mana.',

    // How It Works
    "howItWorks.title": "Cara",
    "howItWorks.titleHighlight": "Kerjanya",
    "howItWorks.subtitle": "Mulai dalam hitungan detik. Tanpa pengaturan rumit, tanpa kurva belajar.",
    "howItWorks.step1.title": "Mulai Chat",
    "howItWorks.step1.desc": "Buka WhatsApp dan mulai percakapan dengan bot Any.do.",
    "howItWorks.step2.title": "Buat Tugas Anda",
    "howItWorks.step2.desc": "Ketik, bicara, atau teruskan pesan. Tambahkan ke daftar mana pun yang Anda mau.",
    "howItWorks.step3.title": "Dapatkan Pengingat",
    "howItWorks.step3.desc": "Terima pesan WhatsApp saat waktunya tiba. Ketuk untuk menunda atau menyelesaikan.",

    // Social Proof
    "socialProof.title": "Dicintai",
    "socialProof.titleHighlight": "Jutaan Orang",
    "socialProof.subtitle": "Bergabunglah dengan komunitas orang-orang yang telah mengubah produktivitas mereka.",
    "socialProof.users": "Pengguna",
    "socialProof.reviews": "Ulasan Bintang 5",
    "socialProof.appStoreRating": "Rating App Store",
    "socialProof.countries": "Negara",
    "socialProof.review1.text": "Akhirnya, aplikasi to-do yang cocok dengan hidup saya! Saya benar-benar tidak pernah keluar dari WhatsApp, jadi ini sempurna.",
    "socialProof.review1.author": "Dewi S.",
    "socialProof.review1.role": "Manajer Pemasaran",
    "socialProof.review2.text": "Fitur teruskan itu jenius. Seseorang mengirim saya sesuatu untuk dilakukan, saya teruskan, selesai. Tidak ada lagi tugas terlupa.",
    "socialProof.review2.author": "Budi P.",
    "socialProof.review2.role": "Pengusaha",
    "socialProof.review3.text": "Saya suka menerima pengingat langsung di WhatsApp. Tidak perlu ganti aplikasi lagi. Mengubah hidup!",
    "socialProof.review3.author": "Siti R.",
    "socialProof.review3.role": "Desainer Freelance",

    // Pricing
    "pricing.badge": "Penawaran Khusus – DISKON 50%",
    "pricing.title": "Mulai Mengatur",
    "pricing.titleHighlight": "Hari Ini",
    "pricing.subtitle": "Dapatkan akses premium dengan harga tak terkalahkan. Waktu terbatas.",
    "pricing.offerEnds": "Penawaran berakhir dalam:",
    "pricing.billedAnnually": "Ditagih tahunan $35.88/tahun",
    "pricing.cta": "Hubungkan WhatsApp →",
    "pricing.cancelAnytime": "Batalkan kapan saja",
    "pricing.feature.unlimitedTasks": "Tugas & pengingat tak terbatas",
    "pricing.feature.whatsapp": "Integrasi WhatsApp",
    "pricing.feature.forward": "Teruskan untuk membuat tugas",
    "pricing.feature.calendar": "Sinkronisasi kalender",
    "pricing.feature.priority": "Dukungan prioritas",
    "pricing.feature.sync": "Sinkronisasi lintas perangkat",
    "pricing.perMonth": "/bulan",

    // FAQ
    "faq.title": "Pertanyaan yang",
    "faq.titleHighlight": "Sering Diajukan",
    "faq.subtitle": "Semua yang perlu Anda ketahui tentang Any.do di WhatsApp.",
    "faq.q1": "Bagaimana Any.do bekerja dengan WhatsApp?",
    "faq.a1": "Cukup kirim pesan ke bot WhatsApp kami untuk membuat tugas, mengatur pengingat, dan mengelola daftar to-do Anda. Anda dapat mengirim pesan suara, meneruskan email atau pesan, dan kami akan otomatis mengubahnya menjadi tugas terorganisir dengan pengingat pintar.",
    "faq.q2": "Apakah data saya privat dan aman?",
    "faq.a2": "Tentu saja. Kami menggunakan enkripsi end-to-end dan tidak pernah membagikan data Anda dengan pihak ketiga. Tugas dan informasi pribadi Anda disimpan dengan aman dan hanya dapat diakses oleh Anda.",
    "faq.q3": "Apakah saya perlu menginstal aplikasi?",
    "faq.a3": "Anda dapat menggunakan Any.do sepenuhnya di WhatsApp tanpa mengunduh apa pun. Namun, aplikasi Any.do menyediakan kemampuan tambahan seperti tampilan kalender, widget, dan akses offline untuk pengalaman yang lebih baik.",
    "faq.q4": "Bisakah saya sinkronisasi dengan kalender saya?",
    "faq.a4": "Ya, Any.do terintegrasi dengan Google Calendar, Outlook, dan Apple Calendar tetapi tidak tersedia melalui integrasi WhatsApp. Tugas dan pengingat Anda akan otomatis tersinkron di semua perangkat dan kalender Anda.",

    // Footer
    "footer.privacy": "Privasi",
    "footer.terms": "Ketentuan",
    "footer.support": "Dukungan",
    "footer.copyright": "Hak cipta dilindungi.",

    // Exit Intent
    "exitIntent.title": "Mulai dalam Hitungan Detik",
    "exitIntent.subtitle": 'Cukup kirim pesan seperti "Ingatkan saya telepon John jam 5 hari ini" dan selesai.',
    "exitIntent.benefit1": "Pesan suara → tugas instan",
    "exitIntent.benefit2": "Pengingat pintar di waktu yang tepat",
    "exitIntent.benefit3": "Bekerja di WhatsApp Anda yang ada",
    "exitIntent.cta": "Hubungkan WhatsApp →",
    "exitIntent.noCreditCard": "Tidak perlu kartu kredit • Batalkan kapan saja",

    // Mobile CTA
    mobileCta: "Hubungkan WhatsApp →",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30",
    "whatsapp.hero.msg2": "✅ Ditambahkan 2 tugas:\n\n📞 **Telepon ibu** - Hari ini jam 18:00\n🎁 **Beli kado ulang tahun**",
    "whatsapp.hero.time2": "10:30",
    "whatsapp.hero.msg3": "🔔 **Telepon ibu** sudah waktunya!",
    "whatsapp.hero.time3": "18:00",

    // WhatsApp Demo Messages
    "whatsapp.demo.msg1": "Tambah susu dan telur ke daftar belanja",
    "whatsapp.demo.time1": "9:15",
    "whatsapp.demo.msg2": "Ditambahkan ke Daftar Belanja: susu, telur 🛒",
    "whatsapp.demo.time2": "9:15",
    "whatsapp.demo.msg3": "Ingatkan saya tentang rapat jam 2 siang",
    "whatsapp.demo.time3": "9:16",
    "whatsapp.demo.msg4": "Pengingat diatur untuk hari ini jam 14:00 📅",
    "whatsapp.demo.time4": "9:16",

    // Notifications
    "notification.justStarted": "baru saja memulai uji coba gratis",
    "notification.minutesAgo": "m lalu",
    "notification.from": "dari",
    "notification.user1.name": "Dewi S.",
    "notification.user1.location": "Jakarta",
    "notification.user2.name": "Budi P.",
    "notification.user2.location": "Surabaya",
    "notification.user3.name": "Siti R.",
    "notification.user3.location": "Bandung",
    "notification.user4.name": "Andi W.",
    "notification.user4.location": "Medan",
    "notification.user5.name": "Putri N.",
    "notification.user5.location": "Semarang",
    "notification.user6.name": "Rizky H.",
    "notification.user6.location": "Makassar",
    "notification.user7.name": "Anita L.",
    "notification.user7.location": "Yogyakarta",
    "notification.user8.name": "Dimas K.",
    "notification.user8.location": "Bali",
  },

  tr: {
    // Header
    "nav.features": "Özellikler",
    "nav.howItWorks": "Nasıl Çalışır",
    "nav.pricing": "Fiyatlandırma",
    "nav.faq": "SSS",
    "nav.cta": "WhatsApp'a Bağlan →",

    // Hero
    "hero.badge": "WhatsApp Hatırlatıcıları",
    "hero.title": "Hiçbir Görevi",
    "hero.titleHighlight": "Asla Unutma",
    "hero.subtitle": "Hatırlatıcıları doğrudan WhatsApp'ta alın. Uygulama değiştirme yok, unutulan görevler yok – sadece mesaj at ve bitti.",
    "hero.cta": "WhatsApp'a Bağlan →",
    "hero.socialProof": "kişi bugün başladı",
    "hero.rating": "651K değerlendirmeden",

    // Features
    "features.title": "Görev Yönetimi,",
    "features.titleHighlight": "Basitleştirildi",
    "features.subtitle": "Düzenli kalmak için ihtiyacınız olan her şey, her gün kullandığınız uygulamanın içinde.",
    "features.justMessage.title": "Sadece Mesaj At",
    "features.justMessage.desc": '"Ayın 1\'inde kirayı ödemeyi hatırlat" – bu kadar basit.',
    "features.voiceTasks.title": "Sesli Görevler",
    "features.voiceTasks.desc": "Sesli mesaj gönderin, biz görevlere dönüştürelim. Doğal konuşun, anlıyoruz.",
    "features.forwardToCreate.title": "Oluşturmak İçin İlet",
    "features.forwardToCreate.desc": "Yapılacak bir şey içeren mesaj mı aldınız? Bota iletin ve görev olsun.",
    "features.multipleTasks.title": "Aynı Anda Birden Fazla Görev",
    "features.multipleTasks.desc": '"Süt al, annemi ara ve raporu gönder" deyin – 3 ayrı görev oluştururuz.',
    "features.snoozeComplete.title": "Ertele ve Tamamla",
    "features.snoozeComplete.desc": "Hatırlatıcıyı ertelemek veya tamamlandı işaretlemek için dokun. WhatsApp'tan çıkmadan kontrolde kal.",
    "features.attachFiles.title": "Dosya Ekle",
    "features.attachFiles.desc": "Görevlerinize fotoğraf, belge ve dosya ekleyin. Her şeyi tek yerde tutun.",
    "features.languages.title": "29+ Dil",
    "features.languages.desc": "Kendi dilinizde konuşun. Türkçe, İngilizce, Arapça ve daha fazlasını destekliyoruz.",
    "features.new": "YENİ",

    // Power Features
    "powerFeatures.badge": "✨ Güçlü Özellikler",
    "powerFeatures.title": "Daha Fazlasını Yap,",
    "powerFeatures.titleHighlight": "Daha Hızlı",
    "powerFeatures.subtitle": "Görev yönetimini zahmetsiz hissettiren gelişmiş yetenekler.",
    "powerFeatures.voiceToTasks.title": "Sesten Göreve",
    "powerFeatures.voiceToTasks.desc": 'Birden fazla görev içeren sesli mesaj gönderin. "Süt al, dişçi randevusu ayarla ve ekibe Cuma toplantısı hakkında e-posta gönder" – anlayacağız ve ayrı görevler oluşturacağız.',
    "powerFeatures.organizeIntoLists.title": "Listelere Düzenle",
    "powerFeatures.organizeIntoLists.desc": '"Alışveriş listeme ekle" veya "Bunu iş görevlerine koy" deyin ve tam istediğiniz yere dosyalayacağız.',
    "powerFeatures.smartSnoozing.title": "Akıllı Erteleme",
    "powerFeatures.smartSnoozing.desc": 'Hazır değil misiniz? "2 saat ertele" veya "Yarın sabah 9\'da hatırlat" dokunun. Basit bir dokunuşla görevleri tamamlayın.',
    "powerFeatures.languageSupport.title": "Kendi Dilinizde Konuşun",
    "powerFeatures.languageSupport.desc": "29+ dilde görev oluşturun. Türkçe, İngilizce, Arapça, Fransızca ve daha fazlası – hepsini anlıyoruz.",
    "powerFeatures.moreLanguages": "+17 daha",
    "powerFeatures.fileAttachments.title": "Dosya Ekleri",
    "powerFeatures.fileAttachments.desc": "Herhangi bir göreve fotoğraf, makbuz, belge ekleyin. Dosyayı gönderin ve hangi göreve ait olduğunu söyleyin.",
    "powerFeatures.smartListAssignment.title": "Akıllı Liste Ataması",
    "powerFeatures.smartListAssignment.desc": '"Alışveriş listeme yumurta ekle" – tam nereye gideceğini biliyoruz.',

    // How It Works
    "howItWorks.title": "Nasıl",
    "howItWorks.titleHighlight": "Çalışır",
    "howItWorks.subtitle": "Saniyeler içinde başlayın. Karmaşık kurulum yok, öğrenme eğrisi yok.",
    "howItWorks.step1.title": "Sohbet Başlat",
    "howItWorks.step1.desc": "WhatsApp'ı açın ve Any.do botu ile sohbet başlatın.",
    "howItWorks.step2.title": "Görevinizi Oluşturun",
    "howItWorks.step2.desc": "Yazın, konuşun veya mesaj iletin. İstediğiniz listeye ekleyin.",
    "howItWorks.step3.title": "Hatırlatma Alın",
    "howItWorks.step3.desc": "Zamanı geldiğinde WhatsApp mesajı alın. Ertelemek veya tamamlamak için dokunun.",

    // Social Proof
    "socialProof.title": "Milyonlarca Kişi",
    "socialProof.titleHighlight": "Tarafından Seviliyor",
    "socialProof.subtitle": "Üretkenliklerini dönüştürmüş insanların topluluğuna katılın.",
    "socialProof.users": "Kullanıcı",
    "socialProof.reviews": "5 Yıldız Değerlendirme",
    "socialProof.appStoreRating": "App Store Puanı",
    "socialProof.countries": "Ülke",
    "socialProof.review1.text": "Sonunda hayatıma uyan bir yapılacaklar uygulaması! WhatsApp'tan hiç çıkmıyorum, bu mükemmel.",
    "socialProof.review1.author": "Ayşe K.",
    "socialProof.review1.role": "Pazarlama Müdürü",
    "socialProof.review2.text": "İletme özelliği dahiyane. Birisi bana yapılacak bir şey gönderdi, iletiyorum, bitti. Artık unutulan görev yok.",
    "socialProof.review2.author": "Mehmet Y.",
    "socialProof.review2.role": "Girişimci",
    "socialProof.review3.text": "Hatırlatıcıları doğrudan WhatsApp'ta almayı seviyorum. Artık uygulama değiştirmek yok. Hayat değiştirici!",
    "socialProof.review3.author": "Zeynep D.",
    "socialProof.review3.role": "Serbest Tasarımcı",

    // Pricing
    "pricing.badge": "Özel Teklif – %50 İNDİRİM",
    "pricing.title": "Bugün Düzenlemeye",
    "pricing.titleHighlight": "Başla",
    "pricing.subtitle": "Rakipsiz fiyata premium erişim alın. Yalnızca sınırlı süre.",
    "pricing.offerEnds": "Teklif şu sürede bitiyor:",
    "pricing.billedAnnually": "Yıllık $35.88/yıl olarak faturalandırılır",
    "pricing.cta": "WhatsApp'a Bağlan →",
    "pricing.cancelAnytime": "İstediğiniz zaman iptal",
    "pricing.feature.unlimitedTasks": "Sınırsız görev ve hatırlatıcı",
    "pricing.feature.whatsapp": "WhatsApp entegrasyonu",
    "pricing.feature.forward": "Görev oluşturmak için ilet",
    "pricing.feature.calendar": "Takvim senkronizasyonu",
    "pricing.feature.priority": "Öncelikli destek",
    "pricing.feature.sync": "Cihazlar arası senkronizasyon",
    "pricing.perMonth": "/ay",

    // FAQ
    "faq.title": "Sıkça Sorulan",
    "faq.titleHighlight": "Sorular",
    "faq.subtitle": "WhatsApp'ta Any.do hakkında bilmeniz gereken her şey.",
    "faq.q1": "Any.do WhatsApp ile nasıl çalışır?",
    "faq.a1": "Görev oluşturmak, hatırlatıcı ayarlamak ve yapılacaklar listenizi yönetmek için WhatsApp botumuza mesaj atmanız yeterli. Sesli mesaj gönderebilir, e-posta veya mesaj iletebilirsiniz ve biz bunları otomatik olarak akıllı hatırlatıcılarla düzenli görevlere dönüştürürüz.",
    "faq.q2": "Verilerim özel ve güvende mi?",
    "faq.a2": "Kesinlikle. Uçtan uca şifreleme kullanıyoruz ve verilerinizi asla üçüncü taraflarla paylaşmıyoruz. Görevleriniz ve kişisel bilgileriniz güvenli bir şekilde saklanır ve yalnızca sizin erişiminize açıktır.",
    "faq.q3": "Uygulama yüklemem gerekiyor mu?",
    "faq.a3": "Any.do'yu tamamen WhatsApp içinde hiçbir şey indirmeden kullanabilirsiniz. Ancak Any.do uygulaması, gelişmiş deneyim için takvim görünümleri, widget'lar ve çevrimdışı erişim gibi ek yetenekler sağlar.",
    "faq.q4": "Takvimimle senkronize edebilir miyim?",
    "faq.a4": "Evet, Any.do Google Takvim, Outlook ve Apple Takvim ile entegre olur ancak WhatsApp entegrasyonu üzerinden kullanılamaz. Görevleriniz ve hatırlatıcılarınız tüm cihazlarınız ve takvimleriniz arasında otomatik olarak senkronize olur.",

    // Footer
    "footer.privacy": "Gizlilik",
    "footer.terms": "Şartlar",
    "footer.support": "Destek",
    "footer.copyright": "Tüm hakları saklıdır.",

    // Exit Intent
    "exitIntent.title": "Saniyeler İçinde Başla",
    "exitIntent.subtitle": '"Bugün saat 5\'te John\'u aramayı hatırlat" gibi bir mesaj gönderin ve hazırsınız.',
    "exitIntent.benefit1": "Sesli mesajlar → anında görevler",
    "exitIntent.benefit2": "Doğru zamanda akıllı hatırlatıcılar",
    "exitIntent.benefit3": "Mevcut WhatsApp'ınızda çalışır",
    "exitIntent.cta": "WhatsApp'a Bağlan →",
    "exitIntent.noCreditCard": "Kredi kartı gerekmiyor • İstediğiniz zaman iptal",

    // Mobile CTA
    mobileCta: "WhatsApp'a Bağlan →",

    // WhatsApp Hero Messages
    "whatsapp.hero.msg1": "",
    "whatsapp.hero.time1": "10:30",
    "whatsapp.hero.msg2": "✅ 2 görev eklendi:\n\n📞 **Annemi ara** - Bugün 18:00\n🎁 **Doğum günü hediyesi al**",
    "whatsapp.hero.time2": "10:30",
    "whatsapp.hero.msg3": "🔔 **Annemi ara** zamanı geldi!",
    "whatsapp.hero.time3": "18:00",

    // WhatsApp Demo Messages
    "whatsapp.demo.msg1": "Alışveriş listesine süt ve yumurta ekle",
    "whatsapp.demo.time1": "9:15",
    "whatsapp.demo.msg2": "Alışveriş Listesine eklendi: süt, yumurta 🛒",
    "whatsapp.demo.time2": "9:15",
    "whatsapp.demo.msg3": "Saat 14:00'teki toplantıyı hatırlat",
    "whatsapp.demo.time3": "9:16",
    "whatsapp.demo.msg4": "Hatırlatıcı bugün 14:00 için ayarlandı 📅",
    "whatsapp.demo.time4": "9:16",

    // Notifications
    "notification.justStarted": "ücretsiz denemeye başladı",
    "notification.minutesAgo": "dk önce",
    "notification.from": "şehrinden",
    "notification.user1.name": "Ayşe K.",
    "notification.user1.location": "İstanbul",
    "notification.user2.name": "Mehmet Y.",
    "notification.user2.location": "Ankara",
    "notification.user3.name": "Zeynep D.",
    "notification.user3.location": "İzmir",
    "notification.user4.name": "Emre B.",
    "notification.user4.location": "Bursa",
    "notification.user5.name": "Elif S.",
    "notification.user5.location": "Antalya",
    "notification.user6.name": "Can T.",
    "notification.user6.location": "Konya",
    "notification.user7.name": "Selin A.",
    "notification.user7.location": "Adana",
    "notification.user8.name": "Burak H.",
    "notification.user8.location": "Gaziantep",
  },
};

export const getTranslation = (locale: Locale, key: string): string => {
  return translations[locale]?.[key] || translations.en[key] || key;
};

export const detectBrowserLocale = (): Locale => {
  const browserLang = navigator.language.split("-")[0];
  const supportedLocales: Locale[] = ["en", "ar", "de", "es", "fr", "he", "hi", "id", "it", "pt", "ru", "tr"];
  return supportedLocales.includes(browserLang as Locale) ? (browserLang as Locale) : "en";
};
